export type NewPlatformTrackerItem = {
  slug: string;
  reviewSlug: string;
  productName: string;
  logoLabel: string;
  image: string;
  category: string;
  readTime: string;
  platformUrl: string;
  platformCtaLabel: string;
  featured: boolean;
  dateLabel: string;
  signalScore: number;
  statusLabel: string;
  price: string;
  description: string;
  bestFor: string;
  reviewTitle: string;
  reviewExcerpt: string;
  reviewSummary: string;
  standoutPoints: string[];
  watchPoints: string[];
  features: string[];
};

export const newPlatformTrackerLastUpdated = {
  formatted: 'Nov 4, 2026',
  iso: '2026-11-04T12:00:00.000Z',
};

const aug2026PlatformImageVersion = 'aug2026';

export const newPlatformTrackerItems: NewPlatformTrackerItem[] = [
  {
    slug: 'eternal-ai',
    reviewSlug: 'eternal-ai-review',
    productName: 'Eternal AI',
    logoLabel: 'EA',
    image: '/eternal-ai-review-cover-v2.webp',
    category: 'AI Dating Companion',
    readTime: '10 min read',
    platformUrl: '/go/eternal-ai',
    platformCtaLabel: 'Visit Eternal AI',
    featured: true,
    dateLabel: 'Nov 2026 tested',
    signalScore: 2.96,
    statusLabel: 'Tested',
    price: 'From $2.49/week (Sweet Start); token packs from $0.99. Prices checked November 3, 2026',
    description:
      'Eternal AI is a mobile-first AI girlfriend app with Tinder-style matching, short character videos, Plays, Flings, and free-token rewards. Desktop is weak and generative tools are limited.',
    bestFor:
      'Casual mobile-only users who want dating-app style matching, short character videos, and free-token rewards more than advanced generative tools or desktop access.',
    reviewTitle: 'Eternal AI Review: Is This Mobile-First AI App Good Enough?',
    reviewExcerpt:
      'Eternal AI is a mobile-first AI girlfriend app with Tinder-style matching, short character videos, and free-token rewards. Chat works, but desktop access is weak, generative tools are limited, and plans are weekly only.',
    reviewSummary:
      'A hands-on Eternal AI test found a fun mobile Match experience, strong character videos, working SFW controls, and free-token rewards that can cover Sweet Start. Desktop feels like a vertical phone layout, generative tools are limited, creator tools are shallow, and billing is weekly only. Early score: 2.96/5.',
    standoutPoints: [
      'Mobile-first Tinder-style Match flow is fun for casual users.',
      'Character videos look strong for a companion app.',
      'Free-token rewards can unlock Sweet Start without paying.',
      'SFW switch worked cleanly in this test.',
    ],
    watchPoints: [
      'Desktop version is not practical.',
      'Generative tools and creator revenue feel shallow.',
      'Weekly subscriptions only.',
      'Match onboarding lacks depth for advanced users.',
    ],
    features: [
      'Chats',
      'Match engine',
      'Plays',
      'Flings',
      'Token rewards',
      'Weekly premium plans',
      'SFW / NSFW switch',
    ],
  },
  {
    slug: 'ai-trans-chat',
    reviewSlug: 'ai-trans-chat-review',
    productName: 'AI Trans Chat',
    logoLabel: 'AT',
    image: '/new-ai-platforms/ai-trans-chat-cover-v3.webp',
    category: 'AI Trans Companion Chat',
    readTime: '7 min read',
    platformUrl: '/go/ai-trans-chat',
    platformCtaLabel: 'Visit AI Trans Chat',
    featured: false,
    dateLabel: 'Nov 2026 tracker',
    signalScore: 4.28,
    statusLabel: 'New scan',
    price:
      'Homepage advertises free start with no credit card. Paid plans, token packs, and generation limits still need a live checkout check',
    description:
      'AI Trans Chat is a browser AI companion platform at ai-trans-chat.com focused on transgender AI girlfriends, character chat, a custom generator, immersive roleplay, and memory. It brands itself around niche trans companions rather than generic girlfriend catalogs.',
    bestFor:
      'Readers who specifically want transgender AI girlfriend chat, custom character creation, and NSFW-leaning roleplay in one niche browser app.',
    reviewTitle: 'AI Trans Chat Review: Trans Companions, Custom Generator, and Roleplay First Look',
    reviewExcerpt:
      'AI Trans Chat offers transgender AI companion chat at ai-trans-chat.com with ready-made characters, a custom generator, roleplay mode, and memory claims. Free start is advertised; paid limits still need a live check.',
    reviewSummary:
      'AI Trans Chat joins this week\'s tracker because ai-trans-chat.com is a niche AI girlfriend site Heaven Girlfriend has not covered yet. The homepage sells dedicated trans companions, a custom AI trans generator, immersive roleplay, encrypted private chats, and a memory engine that supposedly keeps continuity across sessions. Companion cards are image-forward with short bios, and the brand leans adult. Heaven Girlfriend should test whether chat feels less samey than generic NSFW character dumps, whether memory actually sticks after a closed tab, how fast free limits burn, and what live pricing looks like beyond the no-credit-card homepage claim. The review angle is simple: is this a real niche companion product, or a character gallery with trans SEO wrapping?',
    standoutPoints: [
      'Niche transgender AI girlfriend focus is clearer than broad character dumps that bury the category.',
      'Custom generator plus ready-made companions gives both browse and build paths.',
      'Homepage memory and privacy claims are a direct fit for companion-seeking readers.',
      'Image-forward companion cards make the first browse easy to judge before chat testing.',
    ],
    watchPoints: [
      'Chat quality, memory continuity, and whether sessions stay private after a closed tab are still untested.',
      'Paid plans, token burn, and image or generation limits need a live checkout check.',
      'Adult-first branding may be too aggressive for readers wanting softer romance.',
      'Roleplay depth across multiple characters still needs hands-on comparison with broader NSFW chat apps.',
    ],
    features: [
      'Transgender AI companion chat',
      'Custom AI trans generator',
      'Immersive roleplay mode',
      'Memory engine',
      'Private encrypted chats',
      'Ready-made companion catalog',
    ],
  },
  {
    slug: 'lovechat-ai',
    reviewSlug: 'lovechat-ai-review',
    productName: 'LoveChat',
    logoLabel: 'LC',
    image: '/new-ai-platforms/lovechat-ai-cover.webp',
    category: 'NSFW AI Character Chat',
    readTime: '7 min read',
    platformUrl: '/go/lovechat-ai',
    platformCtaLabel: 'Visit LoveChat',
    featured: false,
    dateLabel: 'Oct 2026 tracker',
    signalScore: 4.40,
    statusLabel: 'New scan',
    price:
      'Browse is free behind an 18+ NSFW toggle. Paid access is a token store. Older listicle copy put a pack at $12.99 for 4,000 coins, so checkout needs a live check',
    description:
      'LoveChat is a browser NSFW AI character chat and roleplay platform at lovech.at with creator-made companions and image-ready characters. Browse is free behind an age gate. Paid access is a token store. It is not Lovescape, Lovel.ai, WhatsLove, or GoLove.',
    bestFor:
      'Readers who want NSFW character chat and roleplay, then need a clear split from Lovescape, Lovel, WhatsLove, and GoLove.',
    reviewTitle: 'LoveChat Review: Creator Characters, NSFW Roleplay, and a Love-Name Check',
    reviewExcerpt:
      'LoveChat offers NSFW AI character chat at lovech.at with creator-made companions, roleplay, and image-ready characters. It is not Lovescape, Lovel, WhatsLove, or GoLove.',
    reviewSummary:
      'LoveChat joins this week\'s tracker as the cover because lovech.at is a girlfriend-adjacent NSFW character chat site Heaven Girlfriend only covered in a short listicle, not a dedicated first-look. The homepage sells creator-made companions, roleplay chats, image-ready characters, and an unfiltered toggle behind an 18+ gate. A store page sells a full-experience unlock. Older listicle copy put tokens at $12.99 for 4,000 coins. Keep the names separate: this is not Lovescape, not Lovel.ai, not WhatsLove, and not GoLove. Heaven Girlfriend should test chat quality after the NSFW toggle, whether creator cards stay consistent, how fast tokens burn, and the live store price. The review angle is simple: is this a real companion platform, or a character dump with a love name sitting next to Lovescape?',
    standoutPoints: [
      'Creator-made companions plus NSFW roleplay is a direct Heaven Girlfriend reader fit.',
      'An 18+ unfiltered toggle is a clearer adult path than vague "spicy mode" copy.',
      'Image-ready character cards make the first browse easier to judge than text-only dumps.',
      'Heaven Girlfriend already listed it, so this first-look can check whether chat quality caught up.',
    ],
    watchPoints: [
      'Readers can mix LoveChat at lovech.at with Lovescape, Lovel.ai, WhatsLove, or GoLove unless the review keeps the brands separate.',
      'Token burn and the live store price still need a check against older $12.99 pack copy.',
      'Chat quality, memory, and whether chats stay private after a closed tab are still untested in this first-look.',
      'The catalog is character-roleplay first, so girlfriend continuity may be thinner than dedicated companion apps.',
    ],
    features: [
      'Creator-made AI characters',
      'NSFW unfiltered toggle',
      'Roleplay chat',
      'Image-ready character cards',
      'Character store',
      'Token billing',
    ],
  },
  {
    slug: 'makegirl-ai',
    reviewSlug: 'makegirl-ai-review',
    productName: 'MakeGirl AI',
    logoLabel: 'MG',
    image: '/new-ai-platforms/makegirl-ai-cover.webp',
    category: 'AI Girlfriend Chat',
    readTime: '7 min read',
    platformUrl: '/go/makegirl-ai',
    platformCtaLabel: 'Visit MakeGirl AI',
    featured: false,
    dateLabel: 'Oct 2026 tracker',
    signalScore: 4.41,
    statusLabel: 'New scan',
    price:
      'Free chat with pre-made characters. Image viewing sits behind membership. Custom characters, images, and phone calls sit behind tokens. Older listicle copy put membership under $5/mo, so checkout needs a live check',
    description:
      'MakeGirl AI is a browser NSFW AI girlfriend platform at makegirl.ai with a character builder, chat, phone calls, and an image generator. Pre-made chat is free. Images and custom generation use membership and tokens. It is not MyLovely AI.',
    bestFor:
      'Readers who want a girlfriend builder with chat, images, and calls, then need a dedicated first-look after the listicle blurbs.',
    reviewTitle: 'MakeGirl AI Review: Character Builder, Chat, Images, and a Listicle Check',
    reviewExcerpt:
      'MakeGirl AI offers NSFW girlfriend chat at makegirl.ai with a character builder, image generator, and phone calls. Pre-made chat is free. Images and custom generation are paid.',
    reviewSummary:
      'MakeGirl AI joins this week\'s tracker as the cover because makegirl.ai is a girlfriend-first chat and image site Heaven Girlfriend only covered in short listicles, not a dedicated first-look. The homepage sells custom girlfriends and boyfriends, NSFW chat, image generation, and phone calls. FAQ copy says pre-made characters are free to chat, image viewing needs a membership, and custom girlfriends plus images need tokens. Keep it separate from MyLovely AI. Heaven Girlfriend should test whether personalities actually differ, how fast tokens burn on images and calls, whether memory holds, and the live membership price against older under-$5 listicle copy. The review angle is simple: is the character builder the real product, or does chat still flatten once you leave the listicle screenshots?',
    standoutPoints: [
      'Character builder plus chat, images, and phone calls is a direct Heaven Girlfriend reader fit.',
      'Published free path for pre-made chat makes the first session easier to test than card-walled newcomers.',
      'Character catalogs sit in one browser app, which many image-first sites skip.',
      'Heaven Girlfriend already tested it in listicles, so this first-look can check whether chat quality caught up.',
    ],
    watchPoints: [
      'Older listicles already flagged samey chat across personalities, so the builder may outrun the conversation.',
      'Membership and token prices are split, and the live checkout still needs a check against under-$5 listicle copy.',
      'Image quality, call quality, and whether chats stay private after a closed tab are still untested in this first-look.',
      'Readers can mix MakeGirl AI at makegirl.ai with MyLovely AI unless the review keeps the brands separate.',
    ],
    features: [
      'Custom AI girlfriend and boyfriend builder',
      'NSFW chat and roleplay',
      'AI image generator',
      'Phone calls',
      'Free pre-made chat',
      'Membership and token billing',
    ],
  },
  {
    slug: 'krushchat-ai',
    reviewSlug: 'krushchat-ai-review',
    productName: 'KrushChat',
    logoLabel: 'KC',
    image: '/new-ai-platforms/krushchat-ai-cover.webp',
    category: 'AI Girlfriend Chat',
    readTime: '7 min read',
    platformUrl: '/go/krushchat-ai',
    platformCtaLabel: 'Visit KrushChat',
    featured: false,
    dateLabel: 'Oct 2026 tracker',
    signalScore: 4.42,
    statusLabel: 'New scan',
    price:
      'Free plan: 15 messages a day, 100-word replies, limited photos, and 1K memory. Premium annual copy is $7.77/mo billed $93.24/year, with a $29.99/mo list price still shown, so checkout needs a live check',
    description:
      'KrushChat is a browser and mobile AI girlfriend platform at krush.chat with custom Krushes, chat, voice, photos, roleplay, and memory. Free starts at 15 messages a day. Premium annual copy is $7.77/mo billed yearly. It is not SecretCrush at secretcrush.ai, and it is not VirtualCrush at virtualcrush.ai.',
    bestFor:
      'Readers who want girlfriend chat with photos and voice, then need a clear split from SecretCrush and VirtualCrush.',
    reviewTitle: 'KrushChat Review: Custom Krushes, Photos, Voice, and a Crush-Name Check',
    reviewExcerpt:
      'KrushChat offers AI girlfriend chat at krush.chat with custom companions, photos, voice, roleplay, and a 15-message free start. It is not SecretCrush or VirtualCrush.',
    reviewSummary:
      'KrushChat joins this week\'s tracker as the cover because krush.chat is a girlfriend-first chat site Heaven Girlfriend only covered in short listicles, not a dedicated first-look. The homepage sells custom Krushes, chat, voice, photos, roleplay, and memory, plus iOS and Android apps. Free starts at 15 messages a day with 100-word replies, limited photos, and 1K memory. Premium copy sells unlimited messages, longer replies, NSFW photos, premium voices, 8K memory, and 100 monthly credits, with annual billing shown at $7.77/mo. Keep the names separate: this is not SecretCrush at secretcrush.ai, and it is not VirtualCrush at virtualcrush.ai. Heaven Girlfriend should test photo quality after the free photo cap, whether memory actually holds, how fast 15 free messages burn, and the live checkout against the $7.77 annual banner. The review angle is simple: is this a real companion platform, or a crush-name clone sitting next to SecretCrush?',
    standoutPoints: [
      'Custom Krush builder plus photos, voice, and roleplay is a direct Heaven Girlfriend reader fit.',
      'Published free limits (15 messages, 100 words, 1K memory) make the first session easier to test than vague free claims.',
      'Annual Premium copy of $7.77/mo is a clear price floor versus many unnamed newcomers.',
      'iOS and Android apps sit beside the browser app, which most new scans still skip.',
    ],
    watchPoints: [
      'Readers can mix KrushChat at krush.chat with SecretCrush or VirtualCrush unless the review keeps the domains separate.',
      '15 free messages and limited photos can empty before image quality is clear.',
      'The $7.77/mo annual banner still sits next to a $29.99/mo list price, so checkout needs a live check.',
      'Memory, voice quality, and whether chats stay private after a closed tab are still untested.',
    ],
    features: [
      'Custom Krush creation',
      'AI girlfriend chat and roleplay',
      'Photo requests and galleries',
      'Voice',
      'Memory tiers',
      'iOS and Android apps',
      'Free daily message cap',
      'Premium annual plans',
    ],
  },
  {
    slug: 'heaven-ai',
    reviewSlug: 'heaven-ai-review',
    productName: 'HeavenAI',
    logoLabel: 'HA',
    image: '/new-ai-platforms/heaven-ai-cover.webp',
    category: 'AI Girlfriend Companion',
    readTime: '7 min read',
    platformUrl: '/go/heaven-ai',
    platformCtaLabel: 'Visit HeavenAI',
    featured: false,
    dateLabel: 'Oct 2026 tracker',
    signalScore: 4.44,
    statusLabel: 'New scan',
    price:
      'Free to start with no card; paid plans from $8.59/mo, with checkout still needing a live check against the EUR homepage copy',
    description:
      'HeavenAI is a browser AI girlfriend and boyfriend platform at heavenai.app with custom companions, photos, voice, and evolving relationships, free to start with no card. Paid plans start from $8.59/mo. It is not Heaven Girlfriend, and it is not Neptune AI at neptune-ai.app.',
    bestFor:
      'Readers who want a girlfriend-first companion with photos and voice, then need a clear split from Heaven Girlfriend and from Neptune AI.',
    reviewTitle: 'HeavenAI Review: Girlfriend Chat, Photos, Voice',
    reviewExcerpt:
      'HeavenAI offers AI girlfriend and boyfriend chat at heavenai.app with photos, voice, evolving relationships, and a free start with no card. It is not Heaven Girlfriend.',
    reviewSummary:
      'heavenai.app is a girlfriend-first companion site with a dedicated first-look here. The homepage sells create-your-own girlfriends and boyfriends, photos, voice, and relationships that evolve over time. Signup is free to start with no card. Paid copy starts from $8.59/mo. Keep the brands separate: this is not Heaven Girlfriend, and the same product family also appears as Neptune AI at neptune-ai.app. The review angle is simple: is this a real companion platform, or a lookalike name sitting next to Heaven Girlfriend?',
    standoutPoints: [
      'Girlfriend and boyfriend creation with photos and voice is a direct Heaven Girlfriend reader fit.',
      'Free to start with no card makes the first session easier to test than most card-walled newcomers.',
      'Published paid floor of $8.59/mo is clearer than vague "premium soon" copy.',
      'Evolving-relationship copy is a stronger companion claim than a one-shot character dump.',
    ],
    watchPoints: [
      'The same site family also rebrands as Neptune AI at neptune-ai.app, so domain and billing need a live check.',
      'Photo, voice, and memory quality after the free start are still untested.',
      'EUR homepage pricing can differ from the live USD checkout a reader actually sees.',
    ],
    features: [
      'Custom AI girlfriend and boyfriend creation',
      'Photo generation',
      'Voice',
      'Evolving relationships',
      'Free start with no card',
      'Paid plans from $8.59/mo',
    ],
  },
  {
    slug: 'secretcrush-ai',
    reviewSlug: 'secretcrush-ai-review',
    productName: 'SecretCrush',
    logoLabel: 'SC',
    image: '/new-ai-platforms/secretcrush-ai-cover.webp',
    category: 'NSFW AI Girlfriend Chat',
    readTime: '7 min read',
    platformUrl: '/go/secretcrush-ai',
    platformCtaLabel: 'Visit SecretCrush',
    featured: false,
    dateLabel: 'Oct 2026 tracker',
    signalScore: 4.45,
    statusLabel: 'New scan',
    price:
      '20 free coins on signup for first images; Premium and extra coin packs are sold, with autumn sale copy of up to 55% off Premium and 15% off coins, so checkout needs a live check',
    description:
      'SecretCrush is a browser NSFW AI girlfriend chat platform at secretcrush.ai with custom characters, image and video generation, chat suggestions, Challenge mode, 20 free coins, and Premium coin plans. It is not VirtualCrush at virtualcrush.ai.',
    bestFor:
      'Readers who want uncensored girlfriend chat with images and video, then need a clear answer on coin burn and how it differs from VirtualCrush.',
    reviewTitle: 'SecretCrush Review: Free Coins, NSFW Chat, and a VirtualCrush Check',
    reviewExcerpt:
      'SecretCrush offers NSFW AI girlfriend chat at secretcrush.ai with custom characters, images, videos, 20 free coins, and Premium plans. It is not VirtualCrush.',
    reviewSummary:
      'SecretCrush joins this week\'s tracker as the cover because secretcrush.ai is a girlfriend-first NSFW chat site Heaven Girlfriend only covered in short listicles, not a dedicated first-look. The homepage sells custom AI girlfriends and boyfriends, uncensored chat, image and video generation with sound, chat suggestions after every reply, and Challenge mode that makes attraction something you earn. Signup starts with 20 free coins for first images. Paid paths are Premium plus coin packs, with autumn sale copy of up to 55% off Premium and 15% off coins. Keep the domains separate: this is not VirtualCrush at virtualcrush.ai, and it is not Secrets AI. Heaven Girlfriend should test coin burn on images and video, whether Challenge mode actually changes chat, whether memory holds, and the live checkout price after the sale banner. The review angle is simple: is this a real companion platform, or a coin shop with a familiar crush name?',
    standoutPoints: [
      'Custom girlfriend chat plus image and video in one browser flow is a direct Heaven Girlfriend fit.',
      '20 free coins and a published sale banner make the first session and the paid path easier to test than most vague newcomers.',
      'Chat suggestions and Challenge mode are distinct from a plain character dump.',
      'Video with sound is a clearer media claim than still-image-only new scans.',
    ],
    watchPoints: [
      'Coin burn on images and videos can empty 20 free coins before quality is clear.',
      'Premium and coin-pack prices still need a live checkout against the 55% off sale copy.',
      'Readers can mix this up with VirtualCrush at virtualcrush.ai or Secrets AI unless the review keeps the domains separate.',
      'Challenge mode, memory, and whether chats stay private after a closed tab are still untested.',
    ],
    features: [
      'Custom AI girlfriend and boyfriend chat',
      'NSFW character catalog',
      'Image generation',
      'Video generation with sound',
      'Chat suggestions',
      'Challenge mode',
      '20 free coins on signup',
    ],
  },
  {
    slug: 'yollo-ai',
    reviewSlug: 'yollo-ai-review',
    productName: 'Yollo AI',
    logoLabel: 'YO',
    image: '/new-ai-platforms/yollo-ai-cover.webp',
    category: 'Uncensored Roleplay Suite',
    readTime: '7 min read',
    platformUrl: '/go/yollo-ai',
    platformCtaLabel: 'Visit Yollo AI',
    featured: false,
    dateLabel: 'Oct 2026 tracker',
    signalScore: 4.46,
    statusLabel: 'New scan',
    price:
      'Homepage says core chat, image, and video are free with no sign-up; generous usage is claimed, so paid caps, credits, or a later checkout still need a live check',
    description:
      'Yollo AI is a no-signup uncensored roleplay suite at yollo.ai with 200,000+ characters, custom girlfriend or boyfriend builders, 10+ chat models, long-term memory, and image and video tools sold as free.',
    bestFor:
      'Readers who want a huge character catalog, uncensored girlfriend chat, and image plus video tools without making an account first, then need a clear answer on whether free stays free.',
    reviewTitle: 'Yollo AI Review: 200K Characters, No Sign-Up, and a Free Claim',
    reviewExcerpt:
      'Yollo AI offers 200,000+ uncensored roleplay characters at yollo.ai, custom girlfriend builders, 10+ chat models, memory, and image and video tools with a no-signup free claim.',
    reviewSummary:
      'Yollo AI joins this week\'s tracker as the cover because yollo.ai is a new-to-Heaven Girlfriend uncensored roleplay suite, not a site we already review. The homepage sells 200,000+ characters, a custom girlfriend or boyfriend builder, a persona feature, more than 10 chat models, long-term memory, 4K image tools, and 1080p video templates, all with no sign-up. The loudest claim is that core chat, image, and video are completely free. Heaven Girlfriend should test whether no-account chat actually remembers, how fast free image and video usage hits a wall, whether the catalog is girlfriend-quality or a dump of spicy bots, and if a paid gate appears after a few generations. The review angle is simple: is this a genuine free companion suite, or an uncensored generator with girlfriend chat as the hook?',
    standoutPoints: [
      'A 200,000+ character library plus a custom girlfriend builder is a direct Heaven Girlfriend catalog test.',
      'No sign-up and a free core-feature claim make a first session unusually easy to start.',
      'Persona controls, 10+ models, and long-term memory are more chat depth than a typical image-only newcomer.',
      'Image and video sit on the same site as chat, so the all-in-one pitch can be judged in one account-free pass.',
    ],
    watchPoints: [
      'Free with generous usage is a homepage claim, so image, video, and model limits still need a live usage check.',
      'No sign-up can mean weak memory once the tab closes, even if the page promises long-term memory.',
      'A huge spicy catalog can bury girlfriend-quality companions under low-quality public bots.',
      'Uncensored positioning needs a content-policy and safety-control read before a full score.',
    ],
    features: [
      '200,000+ roleplay characters',
      'Custom girlfriend and boyfriend builder',
      'No sign-up chat',
      '10+ AI chat models',
      'Long-term memory claim',
      'Uncensored image generation',
      'AI video generation',
    ],
  },
  {
    slug: 'ai-allure',
    reviewSlug: 'ai-allure-review',
    productName: 'AI Allure',
    logoLabel: 'AA',
    image: '/new-ai-platforms/ai-allure-cover.webp',
    category: 'Unlimited Media AI Girlfriend',
    readTime: '7 min read',
    platformUrl: '/go/ai-allure',
    platformCtaLabel: 'Visit AI Allure',
    featured: false,
    dateLabel: 'Oct 2026 tracker',
    signalScore: 4.47,
    statusLabel: 'New scan',
    price:
      'Homepage sells unlimited images, videos, live calls, and memory with no tokens; an older Heaven Girlfriend listicle listed Premium around $14.95 a month, so checkout needs a live check',
    description:
      'AI Allure is a browser AI girlfriend platform at aiallure.com with custom companions, memory, named live-room characters, live calls, and a homepage claim of unlimited images and videos with no tokens.',
    bestFor:
      'Readers who want one custom AI girlfriend with chat, photos, video, and live calls, then need a clear answer on what unlimited actually costs.',
    reviewTitle: 'AI Allure Review: Unlimited Images, Live Calls, and a Price Check',
    reviewExcerpt:
      'AI Allure offers custom AI girlfriends at aiallure.com with memory, live calls, Prime Video, and a homepage claim of unlimited images and videos with no tokens.',
    reviewSummary:
      'AI Allure joins this week\'s tracker as the cover because aiallure.com is a girlfriend-first companion site Heaven Girlfriend only covered in a short listicle, not a dedicated first-look. The homepage sells custom girlfriend creation, deep memory, a consistent look across photos and video, live calls, Prime Video, and named live-room characters such as Nivestar Ablaze. The loudest claim is unlimited images and videos with no daily limits and no tokens. The live checkout is the number that counts. Heaven Girlfriend should test whether memory actually holds, whether the same face stays consistent in Prime Video, how live calls feel, and whether unlimited is real or gated after signup. The review angle is simple: is this a genuine companion with unlimited media, or a live-room landing page with a token-free slogan?',
    standoutPoints: [
      'Custom girlfriend creation plus named live-room characters gives both a builder path and a catalog path.',
      'Memory, consistent look, images, Prime Video, and live calls sit in one companion flow.',
      'The no-token unlimited media claim is rarer than coin and heart shops, if checkout confirms it.',
      'Girlfriend, boyfriend, and anime modes make the first session easy to start.',
    ],
    watchPoints: [
      'Unlimited images, videos, and live calls still need a live checkout against the older $14.95 monthly listing.',
      'Prime Video quality, generated audio, and face consistency are homepage claims, not yet tested.',
      'Live rooms and character names can feel more like a feed than a private girlfriend until memory is proven.',
      'The older ranking called it too simple and too expensive, so chat depth still has to earn a better score.',
    ],
    features: [
      'Custom AI girlfriend builder',
      'Memory and relationship context',
      'Unlimited image generation claim',
      'Prime Video scenes',
      'Live calls',
      'Named live-room characters',
      'Girlfriend, boyfriend, and anime modes',
    ],
  },
  {
    slug: 'onlygfs-ai',
    reviewSlug: 'onlygfs-ai-review',
    productName: 'OnlyGFs',
    logoLabel: 'OG',
    image: '/new-ai-platforms/onlygfs-ai-cover.webp',
    category: 'AI Girlfriend Chat and Media',
    readTime: '7 min read',
    platformUrl: '/go/onlygfs-ai',
    platformCtaLabel: 'Visit OnlyGFs',
    featured: false,
    dateLabel: 'Oct 2026 tracker',
    signalScore: 4.48,
    statusLabel: 'New scan',
    price:
      'Free start with limited chats and 20 tokens; Weekly is $4.99 with 100 tokens and unlimited private chats; Monthly is $11.99 with 500 tokens, image and video generation, and all companions',
    description:
      'OnlyGFs is a browser AI girlfriend platform at onlygfs.ai with 50+ named companions, natural chat, roleplay, memory, encrypted private chats, and image and video generation from a free token start.',
    bestFor:
      'Readers who want a named AI girlfriend roster with chat, pictures, and video, and a published weekly or monthly price before they test token burn.',
    reviewTitle: 'OnlyGFs Review: 50+ AI Girlfriends, Chat, Images, and a Token Checkout',
    reviewExcerpt:
      'OnlyGFs offers 50+ named AI girlfriends at onlygfs.ai with chat, roleplay, memory, and image and video generation from a free 20-token start.',
    reviewSummary:
      'OnlyGFs joins this week\'s tracker as the cover because onlygfs.ai is a girlfriend-first companion site that Heaven Girlfriend did not have a dedicated first-look for. The homepage sells 50+ named companions such as Karina Starling and Aiko Himura, natural conversation, creative roleplay, memory across sessions, and custom images and videos in each companion\'s style. Chat starts free with 20 tokens and no card. Paid paths listed on the page are $4.99 a week with 100 tokens or $11.99 a month with 500 tokens, unlimited private chats, and full media. The site also claims end-to-end encryption and a private gallery by default. Heaven Girlfriend should test whether memory actually holds, how fast tokens burn on images and video, whether the encryption claim holds up in the privacy policy, and if the named roster feels like real companions or a media shop with chat. The review angle is simple: is this a genuine girlfriend platform, or a tokened image and video generator with 50 faces?',
    standoutPoints: [
      'A named 50+ girlfriend roster with chat, roleplay, and memory is a direct Heaven Girlfriend fit.',
      'Published Weekly and Monthly prices are clearer than most new scans, so checkout can be compared to the page.',
      'Image and video generation sits inside the companion, not as a separate studio-only product.',
      'Free start with 20 tokens and no card makes a first session easy to test.',
    ],
    watchPoints: [
      'End-to-end encryption is a homepage claim that still needs a privacy-policy and account-level check.',
      'Images and videos spend tokens, so 20 free tokens or 100 weekly tokens can run out before media quality is clear.',
      'The roster is catalog-first, not a deep custom builder, so personality depth may vary by character.',
      'Memory across sessions and whether chats stay private after a closed tab are still untested.',
    ],
    features: [
      '50+ named AI girlfriends',
      'Natural chat and roleplay',
      'Memory across sessions',
      'AI image generation',
      'AI video generation',
      'Encrypted private chats',
      'Free token start',
    ],
  },
  {
    slug: 'cherrypop-ai',
    reviewSlug: 'cherrypop-ai-review',
    productName: 'CherryPop AI',
    logoLabel: 'CP',
    image: '/new-ai-platforms/cherrypop-ai-cover.webp',
    category: 'Story-Driven AI Girlfriend',
    readTime: '9 min read',
    platformUrl: '/go/cherrypop-ai',
    platformCtaLabel: 'Visit CherryPop AI',
    featured: false,
    dateLabel: 'Oct 2026 tracker',
    signalScore: 3,
    statusLabel: 'Tested',
    price: '$20 subscription; tested video cost 400 tokens. Billing period and full token allowance were not confirmed',
    description:
      'CherryPop AI is a story-driven AI girlfriend platform at cherrypop.ai. Chat and character scenarios are the strong point. Image and video generation were weak in testing, and voice chat was waitlisted.',
    bestFor:
      'Users who want story-driven AI conversations, fictional relationship roleplay, and a clean chat experience more than polished visuals or voice.',
    reviewTitle: 'CherryPop AI Review: Strong Story Chat, Weak Visuals',
    reviewExcerpt:
      'CherryPop AI is an AI girlfriend platform focused on story-driven character chat. Conversations and scenarios are the strong point. Image and video generation were inconsistent in this test, and voice chat was not available.',
    reviewSummary:
      'A hands-on CherryPop AI test found strong story-driven chat with Maren Fior, varied character scenarios, and a clean interface. Image generation took about two minutes and did not match the selected character. Customizable video cost 400 tokens, looked around 480p, and only partly followed the prompt. Voice chat was waitlisted. The tester used a $20 subscription. Early score: 3.00/5.',
    standoutPoints: [
      'Story-driven chat with detailed scenes and consistent roleplay.',
      'Varied characters and relationship scenarios to choose from.',
      'Clean, modern interface that is easy to navigate.',
      'Quick replies help keep conversations moving.',
    ],
    watchPoints: [
      'Generated images did not match the selected character.',
      'Video looked low quality and only partly followed the prompt.',
      'Voice chat was only available through a waitlist.',
      'Exact $20 plan duration, token allowance, and top-up pricing still need confirmation.',
    ],
    features: [
      'Story-driven AI character chat',
      'Multiple character scenarios',
      'Quick reply suggestions',
      'AI image generation',
      'Customizable AI video generation',
      'Token-based media',
      'Voice chat waitlist',
    ],
  },
  {
    slug: 'moonmate',
    reviewSlug: 'moonmate-review',
    productName: 'Moonmate',
    logoLabel: 'MM',
    image: '/new-ai-platforms/moonmate-cover.webp',
    category: 'Voice-Call AI Girlfriend',
    readTime: '7 min read',
    platformUrl: '/go/moonmate',
    platformCtaLabel: 'Visit Moonmate',
    featured: false,
    dateLabel: 'Sep 2026 tracker',
    signalScore: 4.49,
    statusLabel: 'New scan',
    price:
      'Free start with limited daily chats; iOS App Store lists MoonMate Pro at $9.99/month and MoonMate Max at $19.99/month; web checkout prices on moonmate.ai still need a live check',
    description:
      'Moonmate is a web and iOS AI girlfriend platform at moonmate.ai with 10,000+ characters, custom companions, memory-powered chat, real-time voice calls, and selfie photo replies for girlfriend and boyfriend roleplay.',
    bestFor:
      'Readers who want a large AI girlfriend and boyfriend catalog with voice calls and memory, then need to test whether the free daily limit and $9.99 / $19.99 iOS prices match the web checkout.',
    reviewTitle: 'Moonmate Review: 10,000 Characters, Voice Calls, and Memory Chat',
    reviewExcerpt:
      'Moonmate offers 10,000+ AI girlfriends and boyfriends, custom characters, memory chat, real-time voice calls, and selfie replies at moonmate.ai.',
    reviewSummary:
      'Moonmate joins this week\'s tracker as the cover because moonmate.ai is a new-to-Heaven Girlfriend AI companion product with web access and a matching MoonMate iOS app. The public pitch is 10,000+ girlfriend and boyfriend characters, custom character creation, memory-powered chat, real-time voice calls, and selfie photo replies. Freemium copy says you can browse and chat with a limited daily allowance, then pay for more. The clearest published prices are on the App Store: MoonMate Pro $9.99 a month and MoonMate Max $19.99 a month. Heaven Girlfriend should test whether free chats last long enough to judge memory, how real the voice calls feel, whether selfie identity stays consistent, and what moonmate.ai actually charges at web checkout versus iOS. The review angle is simple: is this a genuine companion with memory and calls, or a huge character board with a two-tier upsell?',
    standoutPoints: [
      'A 10,000+ girlfriend and boyfriend catalog plus a from-scratch custom character builder is a larger roster than most new tracker entries.',
      'Real-time voice calls and memory-powered chat are the right claims to test on a first-look companion review.',
      'Selfie and photo replies sit in the same chat flow as text, which is closer to a girlfriend product than a separate image studio.',
      'Published iOS Pro and Max prices at $9.99 and $19.99 a month give a concrete starting point while web checkout is verified.',
    ],
    watchPoints: [
      'The moonmate.ai pricing page is thin in public HTML, so web plan names and renewal terms need a live checkout check.',
      'Free daily chat limits can run out before memory, calls, and selfie quality can be judged.',
      'Voice-call quality, latency, and whether memory survives a closed tab are still untested.',
      'iOS in-app purchases and the website may not show the same price, so readers should confirm the store they actually pay in.',
    ],
    features: [
      '10,000+ AI characters',
      'AI girlfriend and boyfriend chat',
      'Custom character creator',
      'Memory-powered conversations',
      'Real-time voice calls',
      'Selfie photo replies',
      'Web and iOS apps',
    ],
  },
  {
    slug: 'desirex-ai',
    reviewSlug: 'desirex-ai-review',
    productName: 'Desirex AI',
    logoLabel: 'DX',
    image: '/new-ai-platforms/desirex-ai-cover.webp',
    category: 'Community Roleplay Chat',
    readTime: '7 min read',
    platformUrl: '/go/desirex-ai',
    platformCtaLabel: 'Visit Desirex AI',
    featured: false,
    dateLabel: 'Sep 2026 tracker',
    signalScore: 4.48,
    statusLabel: 'New scan',
    price:
      'Free catalog with limits; monthly $15.95 (3,000 D-Coins), 3 months $39.95 ($13.32/month, 10,000 D-Coins), 6 months $59.95 ($9.99/month, 20,000 D-Coins); paid plans advertise unlimited characters, a Pro model, deep memory, and discreet billing; checkout still needs a live check',
    description:
      'Desirex AI is a browser community AI companion site at desirex.ai with 1,600+ user-made characters, unfiltered roleplay, a character creator, D-Coins, and paid Pro model plus deep memory. It is a different product from the DesireX iPhone app at desirex.app.',
    bestFor:
      'Readers who want a huge user-made unfiltered roleplay catalog with a clear adults-only policy, then need to test whether ads, D-Coins, and memory hold up after the free browse.',
    reviewTitle: 'Desirex.ai Review: Strict Adults-Only Rules',
    reviewExcerpt:
      'Desirex.ai offers 1,600+ community AI companions, unfiltered roleplay, a creator, D-Coins, and paid Pro memory at desirex.ai, separate from the DesireX iPhone app.',
    reviewSummary:
      'Desirex AI joins this week\'s tracker as the cover because desirex.ai is a new-to-Heaven Girlfriend first-look, even though older listicles already mention Desirex as a small free roleplay site. The live product is a browser community catalog with 1,600+ user-made companions, NSFW and SFW filters, a character creator, and a strict adults-only, fictional-characters policy, with real-person clones banned. Paid plans reported on the subscribe flow are $15.95 a month, $13.32 a month on three months, or $9.99 a month on six months, with D-Coins, a Pro model, and deep memory. Heaven Girlfriend should test whether free clicks get hijacked by third-party ads, what D-Coins actually buy, whether memory holds across sessions, and that this is desirex.ai, not the DesireX iPhone app at desirex.app. The review angle is simple: is this a genuine companion library with responsible rules, or an ad-heavy character board using a familiar name?',
    standoutPoints: [
      'A 1,600+ user-made catalog with tag, gender, and NSFW filters is larger than the old listicle blurbs suggested.',
      'The creator page states a clear adults-only ban on non-consent, bestiality, and real-person clones.',
      'Published monthly, 3-month, and 6-month prices with D-Coins, a Pro model, and deep memory are easier to compare than a vague free-only pitch.',
      'Discreet billing and cancel-anytime copy match what most readers want from an unfiltered companion site.',
    ],
    watchPoints: [
      'Older Heaven Girlfriend listicles treated Desirex as a tiny free roleplay site; this first-look has to test the live 1,600+ catalog instead of those blurbs.',
      'Free-tier click hijacks to third-party ads are a documented problem and can make the browse experience feel untrustworthy.',
      'D-Coin burn rate, whether the Pro model is actually better, and whether deep memory survives a closed tab need a paid check.',
      'desirex.ai can be confused with the DesireX iPhone app at desirex.app unless the review keeps the domains separate.',
    ],
    features: [
      '1,600+ community AI companions',
      'Unfiltered roleplay chat',
      'Character creator',
      'NSFW and SFW filters',
      'D-Coins economy',
      'Pro AI model',
      'Deep conversation memory',
    ],
  },
  {
    slug: 'promptchan',
    reviewSlug: 'promptchan-review',
    productName: 'Promptchan',
    logoLabel: 'PC',
    image: '/new-ai-platforms/promptchan-cover.webp',
    category: 'Image-First AI Girlfriend',
    readTime: '7 min read',
    platformUrl: '/go/promptchan',
    platformCtaLabel: 'Visit Promptchan',
    featured: false,
    dateLabel: 'Sep 2026 tracker',
    signalScore: 4.47,
    statusLabel: 'New scan',
    price:
      'Free daily Gems with no card; Plus $14.99/month (100 Gems), Premium $23.99/month (400 Gems), Pro $33.99/month (800 Gems plus unlimited casual generations); voice message 1 Gem, voice call 2 Gems; extra Gems 50% off on paid plans',
    description:
      'Promptchan is an image-first AI girlfriend platform at promptchan.com with no-filter chat, a 20 million plus community gallery, clone-and-vary tools, custom characters, and paid Gems for HD images, video, voice, and calls.',
    bestFor:
      'Readers who want uncensored AI girlfriend images and video first, then chat, voice, and custom characters on the same promptchan.com account.',
    reviewTitle: 'Promptchan Review: Adult Chat, 20M Gallery, and Gem-Priced Images',
    reviewExcerpt:
      'Promptchan offers no-filter AI girlfriend chat, a huge community gallery, clone tools, custom characters, and Gem-priced images, video, and voice at promptchan.com.',
    reviewSummary:
      'Promptchan joins this week\'s tracker as the cover because promptchan.com is a full AI girlfriend product we have not reviewed before, even though an older chat listicle mentioned it only in passing. The homepage sells no-filter girlfriend chat, custom characters, and a 20 million plus image and video gallery you can clone. Official pricing on the upgrade page is clearer than most newcomers: free daily Gems, then Plus $14.99, Premium $23.99, and Pro $33.99 a month. Pro is the feature step for full-length video with Extend, custom characters, voice calls, 4K images, and unlimited casual generations. Heaven Girlfriend should test whether free Gems are enough to judge chat, how fast HD stills and video burn the monthly balance, whether cloned gallery faces stay consistent in conversation, and if voice calls feel like a companion or a two-Gem upsell. Consent first: only upload photos of yourself, or of someone who has given clear permission. Creating sexual images of a real person without consent is harmful and illegal in many countries. The review angle is simple: is this a girlfriend platform with memory, or the strongest uncensored image generator in the category with chat attached?',
    standoutPoints: [
      'Official Plus, Premium, and Pro prices from $14.99 a month are published, not hidden behind a vague token menu.',
      'A 20 million plus community gallery plus one-click clone tools make image quality the public product, not a side tab.',
      'No-filter chat, custom characters, HD and 4K stills, full-length video, and voice calls sit on one web, iOS, and Android stack.',
      'A free daily Gem allowance with no card is a real on-ramp before paying for Pro video and custom characters.',
    ],
    watchPoints: [
      'An older listicle treated chat as a side feature at about $14 a month. This first look has to test whether companion chat and memory now hold up next to generation.',
      'Chat, voice, images, and video all spend Gems, so the free daily allowance can run out before you can judge the girlfriend experience.',
      'Custom characters, full-length video, Extend, and voice calls sit on Pro, which can hide the true cost of the full stack.',
      'Uncensored generation and commercial-use claims need a live terms and safety-control check before ranking it against girlfriend-first apps.',
    ],
    features: [
      'No-filter AI girlfriend chat',
      '20M+ community gallery',
      'Clone and vary images',
      'Custom AI characters',
      'HD and 4K image generation',
      'Full-length AI video',
      'Voice messages and calls',
    ],
  },
  {
    slug: 'camsoda-ai',
    reviewSlug: 'camsoda-ai-review',
    productName: 'CamSoda AI',
    logoLabel: 'CS',
    image: '/new-ai-platforms/camsoda-ai-cover.webp',
    category: 'Live-style AI Video',
    readTime: '7 min read',
    platformUrl: '/go/camsoda-ai',
    platformCtaLabel: 'Visit CamSoda AI',
    featured: false,
    dateLabel: 'Sep 2026 tracker',
    signalScore: 2.9,
    statusLabel: 'Tested',
    price: '$10 for 100 tokens. Billing period and the token cost of a 20-second video were not confirmed in the test',
    description:
      'CamSoda.ai is the AI girlfriend app at camsoda.ai, with character chat, spicy mode, custom image prompts, and realistic AI video. This is not the live CamSoda cam site.',
    bestFor:
      'Readers who want realistic AI video and visual experiments more than a story-driven companion.',
    reviewTitle: 'CamSoda.ai Review: Realistic Video, Thin Chat',
    reviewExcerpt:
      'CamSoda.ai offers AI girlfriend chat, image prompts, and realistic video at camsoda.ai. Chat and storytelling were thin in this test, and the image test was not finished.',
    reviewSummary:
      'CamSoda.ai is the AI app at camsoda.ai, not the live cam site. A hands-on test found realistic video as the clear strength, with character variety and custom image prompts also available. Chat was generic, storytelling was thin, and the video felt disconnected from the conversation. The tester paid $10 for 100 tokens and unlocked about 20 seconds of video. The image-generation test was cut off, so image quality is not scored.',
    standoutPoints: [
      'Video looked realistic and was the strongest part of the test.',
      'Characters cover different appearances and scenarios.',
      'Custom image prompts sit alongside preset prompts.',
      'The layout is familiar and fairly easy to navigate.',
    ],
    watchPoints: [
      'Keep this review on the AI app at camsoda.ai, separate from the live cam site.',
      'Chat lacked backstory, emotional depth, and a sense of an ongoing relationship.',
      'The realistic video did not match the ongoing conversation.',
      'Image quality, token burn, video length limits, and the $10 plan billing period still need a full check.',
    ],
    features: [
      'AI character selection',
      'Text chat and spicy mode',
      'Preset and custom image prompts',
      'AI video unlocks',
      'Token-based access',
    ],
  },
  {
    slug: 'soulmate',
    reviewSlug: 'soulmate-review',
    productName: 'Soulmate',
    logoLabel: 'SM',
    image: '/new-ai-platforms/soulmate-cover.webp',
    category: 'Spicy Companion Chat',
    readTime: '7 min read',
    platformUrl: '/go/soulmate',
    platformCtaLabel: 'Visit Soulmate',
    featured: false,
    dateLabel: 'Sep 2026 tracker',
    signalScore: 4.45,
    statusLabel: 'New scan',
    price:
      'Free spicy chat with no account required; subscriptions unlock voice calls, video calls, photo generation, longer memory, and mature content. Checkout prices are not listed on the public page.',
    description:
      'Soulmate is a browser AI companion site at soulmate.io that markets itself as Spicy Chat AI, with 1,000+ named companions, free no-signup chat, roleplay worlds, a companion studio, and paid voice, video, and photo extras. It is a different product from spicychat.ai.',
    bestFor:
      'Readers who want to start a spicy AI girlfriend chat instantly, then test whether voice calls, video, and memory hold up after the free no-account intro.',
    reviewTitle: 'Soulmate.io Review: Free No-Signup Chat, Calls, and a Companion Studio',
    reviewExcerpt:
      'Soulmate.io offers free spicy AI companion chat with no account, 1,000+ named characters, roleplay worlds, a companion studio, and paid voice, video, and photos.',
    reviewSummary:
      'Soulmate joins this week\'s tracker as the cover because soulmate.io is a new-to-Heaven Girlfriend spicy companion site, not the Spicy Chat AI we already review at spicychat.ai. The homepage sells 1,000+ named companions such as Clara Jensen, Sloane Rivers, and Elena Voss, plus roleplay worlds, realistic, anime, and 3D styles, and a companion studio for look, personality, and voice. Chat starts free with no sign-up. Paid unlocks listed in the FAQ are voice calls, video calls, photo generation, longer memory, and mature content. Heaven Girlfriend should test whether no-account chat stays usable, how real the calls feel, whether memory actually persists after closing the tab, and what the subscription costs at checkout. The review angle is simple: is this a genuine companion platform, or a spicy chat landing page using a familiar brand name?',
    standoutPoints: [
      'Free chat with no account is a lower-friction start than most AI girlfriend sites.',
      'A large named cast, roleplay worlds, and realistic, anime, and 3D styles cover more than one girlfriend fantasy.',
      'Voice calls, video calls, photos, and a companion studio are listed as the paid upgrade path.',
      'Persistent memory is the public pitch, which is the right claim to test on a first-look review.',
    ],
    watchPoints: [
      'The site brands itself as Spicy Chat AI, so readers can confuse it with spicychat.ai unless the review keeps the domains separate.',
      'Subscription prices for calls, photos, and mature content are not published on a public pricing page and need checkout verification.',
      'No-signup chat can be a demo that gets gated after a few messages.',
      'Voice and video call quality, lip-sync, and whether memory survives a closed tab need hands-on testing.',
    ],
    features: [
      'Free no-signup spicy chat',
      '1,000+ named AI companions',
      'Roleplay worlds',
      'Companion studio',
      'Realistic, anime, and 3D styles',
      'Voice and video calls',
      'Persistent conversation memory',
    ],
  },
  {
    slug: 'heygf-ai',
    reviewSlug: 'heygf-ai-review',
    productName: 'HeyGF.ai',
    logoLabel: 'HG',
    image: '/new-ai-platforms/heygf-ai-cover.webp',
    category: 'Realistic AI Girlfriend Chat',
    readTime: '7 min read',
    platformUrl: '/go/heygf-ai',
    platformCtaLabel: 'Visit HeyGF.ai',
    featured: false,
    dateLabel: 'Sep 2026 tracker',
    signalScore: 4.44,
    statusLabel: 'New scan',
    price:
      'Free basic chat with no credit card; Casual $9.99/month (150 coins), Closer $19.99/month (350 coins), Exclusive $39.99/month (800 coins); annual from about $8.33/month; coins pay for photos and videos; full photo galleries listed as annual-only',
    description:
      'HeyGF.ai is a browser AI girlfriend site at heygf.ai with a handcrafted realistic companion roster, a custom girlfriend builder, free basic chat, and premium photos, videos, voice messages, and NSFW media paid with coins.',
    bestFor:
      'Readers who want a photorealistic AI girlfriend with free chat first, then pay only if custom photos, videos, and intimate media are the main draw.',
    reviewTitle: 'HeyGF.ai Review: Free Realistic Chat, Custom Girlfriends, and Coin-Gated Media',
    reviewExcerpt:
      'HeyGF.ai offers free AI girlfriend chat, a handcrafted realistic roster, a custom builder, and premium photos, videos, and voice on heygf.ai.',
    reviewSummary:
      'HeyGF.ai joins this week\'s tracker as the cover because heygf.ai is a direct AI girlfriend product: browse handcrafted companions such as Kayla, Freya, Lily, and Madison, or build one from looks, personality, voice, and backstory in the browser. The public pitch is photorealistic chat plus custom photos, videos, and voice, with a free no-card start. Paid ladders are clearer than most new sites: Casual $9.99, Closer $19.99, and Exclusive $39.99 a month, with coins for media and annual gallery unlocks. Heaven Girlfriend should test whether free chat stays usable after signup, how fast coins burn on photos and video, whether Girlfriend Studio identity stays consistent, and if the "earn her attention" mood system feels like chemistry or a paywall. No native app is required. The review angle is simple: is this a real companion with memory, or a realistic photo generator wrapped in girlfriend chat?',
    standoutPoints: [
      'Free basic chat with no credit card is a real on-ramp, not a five-message teaser in the public copy.',
      'Handcrafted realistic companions sit beside a custom builder for looks, personality, voice, and story.',
      'Photos, videos, voice messages, and a Girlfriend Studio give it a full visual girlfriend stack.',
      'Published Casual, Closer, and Exclusive prices from $9.99 a month are easier to compare than vague token menus.',
    ],
    watchPoints: [
      'Unlimited chat, photos, and NSFW sit on paid plans, so the free tier may be text-only after a short sample.',
      'Coins pay for generated media, and public coin counts vary by listing, so checkout burn rate needs a live test.',
      'Full photo galleries are advertised as annual-only, which can hide the true monthly value.',
      'Self-reported 50,000+ users and a 4.8 rating should not be treated as independent proof until chat quality is tested.',
    ],
    features: [
      'Handcrafted AI girlfriend roster',
      'Custom girlfriend builder',
      'Free basic chat',
      'AI photos and videos',
      'Voice messages',
      'Girlfriend Studio prompts',
      'Persistent conversation memory',
    ],
  },
  {
    slug: 'whatslove-ai',
    reviewSlug: 'whatslove-ai-review',
    productName: 'WhatsLove AI',
    logoLabel: 'WL',
    image: '/new-ai-platforms/whatslove-ai-cover-v6.webp',
    category: 'Video-First AI Girlfriend',
    readTime: '7 min read',
    platformUrl: '/go/whatslove-ai',
    platformCtaLabel: 'Visit WhatsLove AI',
    featured: false,
    dateLabel: 'Sep 2026 tracker',
    signalScore: 4.42,
    statusLabel: 'New scan',
    price:
      'Free start with limited messages; directory listings show Standard at $13.99/month (1,000 messages, 15 video nodes) and Premium at $35.99/month (unlimited messages, 50 video nodes); the site FAQ still cites $39.99/month, so checkout needs verification',
    description:
      'WhatsLove AI is a browser AI girlfriend site at whatslove.ai built around context-aware video replies, a Video Plaza of short companion clips, custom and prebuilt female and male partners, persistent chat memory, scene switching, and LOVE Coin wardrobe unlocks.',
    bestFor:
      'Readers who want a video-first AI girlfriend and need a clear answer on whether the clips feel like a live call or a generated 10 to 30 second reply.',
    reviewTitle: 'WhatsLove AI Review: Real-Time Video Call or Context Clips?',
    reviewExcerpt:
      'WhatsLove AI offers video-first AI girlfriends and boyfriends at whatslove.ai, with context-aware short clips, chat memory, scene switching, and paid video nodes.',
    reviewSummary:
      'WhatsLove AI joins this week\'s tracker as the cover because whatslove.ai is one of the few new companion sites that sells video as the product, not a side feature. The homepage is a character plaza with Live Video Mode companions such as Tiffany Blake and Elise Laurent, plus female and male catalogs, Discord, and an 18+ fictional-character disclaimer. Marketing copy talks about real-time video chat, while third-party listings describe 10 to 30 second context clips that burn video nodes (15 on Standard, 50 on Premium). Heaven Girlfriend should test clip length, lip-sync, identity consistency, whether chat memory actually carries across sessions, and which price is real at checkout: $13.99 / $35.99 in directories, or the $39.99 FAQ figure. No native iOS or Android app is confirmed. The review angle is simple: is this a live companion call, or a polished clip generator with a girlfriend wrapper?',
    standoutPoints: [
      'Video Plaza and Live Video Mode put generated companion clips at the center of the product, not behind a buried media tab.',
      'Female and male AI partners, scene switching, and claimed persistent memory make it more than a one-shot video toy.',
      'A free start plus Standard and Premium ladders give readers a path to test chat before paying for extra video nodes.',
      'Browser-only access with multilingual UI (English, French, German, Spanish, Italian, Portuguese) lowers the install barrier.',
    ],
    watchPoints: [
      'Real-time video language needs to be checked against actual clip length, delay, and whether the companion can hold a live back-and-forth.',
      'Video nodes cap usage (listings cite 15/month on Standard and 50/month on Premium), so value depends on how fast those credits burn.',
      'Public pricing disagrees with itself: directories show $13.99 and $35.99, while the official FAQ still says $39.99 per month.',
      'No native mobile app is confirmed, and voice calling is not a proven feature in public listings.',
    ],
    features: [
      'Context-aware AI video clips',
      'Video Plaza companion feed',
      'Female and male AI partners',
      'Custom character creation',
      'Persistent chat memory',
      'Scene switching',
      'LOVE Coin wardrobe unlocks',
    ],
  },
  {
    slug: 'virtualgf',
    reviewSlug: 'virtualgf-review',
    productName: 'VirtualGF',
    logoLabel: 'VG',
    image: '/new-ai-platforms/virtualgf-cover-v2.webp',
    category: 'Free AI Girlfriend Chat',
    readTime: '7 min read',
    platformUrl: '/go/virtualgf',
    platformCtaLabel: 'Visit VirtualGF',
    featured: false,
    dateLabel: 'Sep 2026 tracker',
    signalScore: 4.43,
    statusLabel: 'New scan',
    price:
      'Free unlimited text chat; image credit packs at $11.99 (80 credits), $24.99 (1,000), or $59.99 (5,000); media tokens also cover voice notes and video; pay by card-to-crypto, crypto wallet, or Telegram Stars',
    description:
      'VirtualGF is a browser AI girlfriend site at virtualgf.chat with a custom girlfriend builder, a large realistic and anime catalog, unlimited free text chat, optional NSFW after age checks, and paid tokens for images, voice notes, and video.',
    bestFor:
      'Users who want a free customizable AI girlfriend for unlimited text first, then pay only if they want generated photos, voice notes, or video.',
    reviewTitle: 'VirtualGF Review: Free Unlimited Chat, Custom Girlfriends, and Image Credits',
    reviewExcerpt:
      'VirtualGF offers free unlimited AI girlfriend chat, custom realistic and anime companions, and credit packs for images, voice notes, and video on virtualgf.chat.',
    reviewSummary:
      'VirtualGF joins this week\'s tracker as the cover because virtualgf.chat sells the rare thing in this category: unlimited text chat that the FAQ and terms both treat as free, with a custom girlfriend builder and a big prebuilt realistic plus anime catalog sitting next to it. FutureAI Limited operates the site from Cyprus. The catch is media. Images, voice notes, and video sit behind tokens, and the live checkout is credit packs from $11.99 for 80 images up to $59.99 for 5,000, plus a Plus upsell on the homepage. Heaven Girlfriend should test whether free chat stays uncapped after signup, how strong memory and NSFW feel in practice, whether credit packs auto-renew like the terms suggest, and whether voice and video are real features or just listed in the legal copy.',
    standoutPoints: [
      'Unlimited free text chat is the core product, not a short demo.',
      'Custom girlfriend builder sits beside a large realistic and anime catalog.',
      'Personality, interests, and conversation style can be tuned before you chat.',
      'Images, voice notes, and video exist as paid extras instead of gating the whole app.',
    ],
    watchPoints: [
      'The homepage Plus banner and credit packs sit next to a "completely free" FAQ claim, so media and speed still cost money.',
      'Terms mention subscriptions while checkout shows one-time credit packs, so billing needs verification.',
      'No native iOS or Android app is confirmed; the product is browser-first.',
      'Chat quality, long-term memory, NSFW filters, and whether voice or video actually ship need hands-on testing.',
    ],
    features: [
      'Custom AI girlfriend builder',
      'Realistic and anime companions',
      'Unlimited free text chat',
      'Personality and chat-style controls',
      'Optional NSFW with age verification',
      'AI image generation credits',
      'Voice notes and video tokens',
    ],
  },
  {
    slug: 'magi-ai',
    reviewSlug: 'magi-ai-review',
    productName: 'MAGI AI',
    logoLabel: 'MA',
    image: '/new-ai-platforms/magi-ai-cover-v2.webp',
    category: 'Uncensored AI Girlfriend Studio',
    readTime: '7 min read',
    platformUrl: '/go/magi-ai',
    platformCtaLabel: 'Visit MAGI AI',
    featured: false,
    dateLabel: 'Aug 2026 tracker',
    signalScore: 4.4,
    statusLabel: 'New scan',
    price:
      'Free start with limited credits; monthly from $5.99 (900 credits), $9.99 (1,500), $14.99 (2,250); one-time Lite $15, Starter $50, Premium $100 (credits valid 1 year)',
    description:
      'MAGI AI is a browser-based uncensored AI girlfriend studio at magi1.ai with Spicy Chat roleplay, a free character generator, prebuilt companions, custom portraits, and image-to-video tools that animate an AI girlfriend in POV scenes. Paid monthly and one-time credit packs unlock voice replies, longer chats, watermark-free media, and premium generation models.',
    bestFor:
      'Users who want an uncensored AI girlfriend with strong image and video creation, custom or anime characters, and private Spicy Chat in the browser.',
    reviewTitle: 'MAGI AI Review: Roleplay Chat, Custom Characters, and Image-to-Video',
    reviewExcerpt:
      'MAGI AI offers uncensored Spicy Chat, a free AI girlfriend generator, custom and anime companions, AI portraits, and image-to-video tools on magi1.ai.',
    reviewSummary:
      'MAGI AI stays on this week\'s tracker because it is more than a spicy chatbot: magi1.ai pairs uncensored roleplay with a creator-style image and video suite, including image-to-video POV scenes, custom girlfriend and anime builders, and prebuilt characters such as Regina White and Luna Rose. The free tier is a real starting point for chat and limited generation, then monthly plans from $5.99 and one-time credit packs unlock voice replies, watermark-free media, and premium models. Heaven Girlfriend should test how fast credits burn on video, whether character identity stays consistent across portraits and clips, how strong long-form memory feels after a week, and whether the advertised unlimited Spicy Chat holds on paid plans.',
    standoutPoints: [
      'Image-to-video and POV animation go further than most AI girlfriend chat sites.',
      'Custom girlfriend and anime builders sit beside prebuilt Spicy Chat characters.',
      'Uncensored roleplay, private chat history controls, and no-install browser access.',
      'Monthly pricing from $5.99 and one-time credit packs give more than one billing path.',
    ],
    watchPoints: [
      'Free credits are small, so image and especially video use can push a paid plan quickly.',
      'Video generation can be slow and expensive relative to still-image credits.',
      'No native iOS or Android app; the product is browser-only.',
      'Long-term memory, watermark-free output, and voice replies need paid-plan verification.',
    ],
    features: [
      'Roleplay chat',
      'Free AI girlfriend generator',
      'Custom and anime character builders',
      'AI image generation',
      'Image-to-video POV scenes',
      'Voice replies on paid plans',
      'Prebuilt companion catalog',
    ],
  },
  {
    slug: 'selira-ai',
    reviewSlug: 'selira-ai-review',
    productName: 'Selira AI',
    logoLabel: 'SA',
    image: '/new-ai-platforms/selira-ai-cover-v3.webp',
    category: 'Free-Tier NSFW AI Girlfriend',
    readTime: '7 min read',
    platformUrl: '/go/selira-ai',
    platformCtaLabel: 'Visit Selira AI',
    featured: false,
    dateLabel: 'Aug 2026 tracker',
    signalScore: 4.39,
    statusLabel: 'New scan',
    price: 'Free unlimited chat; Lite from $5.99/mo; Basic $9.99/mo; Premium $19.99/mo; Platinum $39.99/mo (promotional rates)',
    description:
      'Selira AI is a browser-based NSFW AI girlfriend platform at selira.ai with free adult chat, custom companion creation, face-consistent image generation, short AI video, a TikTok-style feed, and a Live Video beta. Paid Lite through Platinum plans add image and video credits, longer memory, and live cam, while the free tier already lets users talk without a message cap.',
    bestFor:
      'Users who want to try an adult AI girlfriend for free, then pay only if they need fast face-consistent images, short videos, or live video.',
    reviewTitle: 'Selira AI Review: Unlimited Free Adult Chat, Face-Consistent Images, and Live Video',
    reviewExcerpt:
      'Selira AI offers unlimited free adult chat with 1,000+ companions, custom character creation, fast face-consistent images, short AI video, and a live video beta on selira.ai.',
    reviewSummary:
      'Selira AI stays on this week\'s tracker because its free tier is the actual product, not a five-message demo: a free chat tier, a large companion catalog, and custom character creation without a credit card. The paid ladder then unlocks image and video credits, Live Video, and longer memory, with Lite promotional pricing from $5.99 a month. Heaven Girlfriend should test whether FaceID keeps the same companion across poses, how quickly image credits burn, whether Live Video feels like a real feature or a beta tease, and whether promotional checkout prices hold after the first billing cycle.',
    standoutPoints: [
      'The free chat tier is usable without a message cap or credit card.',
      'Face-consistent image generation keeps the same companion across poses, outfits, and scenes.',
      'Image and video generation is fast, with realistic and anime pipelines in one account.',
      'Lite promotional pricing from $5.99/month is aggressive for unlocking media credits and Live Video.',
    ],
    watchPoints: [
      'No voice messages or phone calls, so audio-first users will need another platform.',
      'Free image credits are tiny; media use quickly requires Lite or a credit pack.',
      'Long-term memory sits on Premium and above, not the free or Lite plans.',
      'Checkout shows steep promotional discounts and a hard cancel-flow offer, so renewal pricing needs verification.',
    ],
    features: [
      'Free chat tier',
      'Custom AI girlfriend builder',
      'Face-consistent image generation',
      'Short AI video generation',
      'Live Video beta',
      'TikTok-style companion feed',
      'Realistic and anime styles',
    ],
  },
  {
    slug: 'dream-companion',
    reviewSlug: 'dream-companion-review',
    productName: 'Dream Companion',
    logoLabel: 'DC',
    image: `/new-ai-platforms/dream-companion-${aug2026PlatformImageVersion}.webp`,
    category: 'NSFW AI Girlfriend Platform',
    readTime: '7 min read',
    platformUrl: '/go/dream-companion',
    platformCtaLabel: 'Visit Dream Companion',
    featured: false,
    dateLabel: 'Aug 2026 tracker',
    signalScore: 4.38,
    statusLabel: 'New scan',
    price: 'Free tier; Premium from $5.84/mo (annual) or $11.99/mo; Ultimate from $24.99/mo (annual)',
    description:
      'Dream Companion is a browser-based AI girlfriend platform at mydreamcompanion.com where users create custom companions, chat with 1,000+ templates, and generate uncensored images, videos, and voice messages. Built for adult roleplay and long-term virtual relationships, it combines Fast & Easy and Pro Workshop character builders with a Dream Coins economy for media generation.',
    bestFor:
      'Users who want a polished AI girlfriend website with strong visual output, deep character customization, NSFW roleplay, and image plus video generation in one subscription.',
    reviewTitle: 'Dream Companion Review: AI Girlfriends, Dream Coins, Images, and Video',
    reviewExcerpt:
      'Dream Companion is an adult AI girlfriend platform with custom character creation, uncensored chat, AI images, videos, voice messages, and tiered memory on mydreamcompanion.com.',
    reviewSummary:
      'Dream Companion replaces OutPeach on our tracker. It already sits among the strongest visual-first companion websites. The platform combines Fast & Easy and Pro Workshop builders, 1,000+ character templates, NSFW roleplay, in-chat image and video generation, and voice playback through a freemium model with Premium and Ultimate tiers. Heaven Girlfriend should test whether Dream Coins run out too quickly on Premium, how consistent character identity stays across generated media, and whether annual billing value holds up against Candy AI and Kupid AI.',
    standoutPoints: [
      'Pro Workshop and Fast & Easy builders support both quick and deep AI girlfriend creation.',
      'In-chat uncensored images, NSFW video generation, and voice messages create a full multimodal companion flow.',
      'Ultra long-term memory on Ultimate fits users seeking ongoing virtual relationships.',
      'Annual Premium pricing under $6/month makes it one of the better value visual AI girlfriend platforms.',
    ],
    watchPoints: [
      'Dream Coins (5 per image, 20 per video) can drain quickly on the 100-coin Premium allowance.',
      'No native iOS or Android app; browser-only access may limit mobile-first users.',
      'Subscription refunds are limited; annual billing should be tested before committing.',
      'Coin packs and renewal pricing need checkout verification against promotional banners.',
    ],
    features: [
      'Custom AI girlfriend builder',
      '1,000+ character templates',
      'Uncensored NSFW chat',
      'AI image and video generation',
      'Voice messages',
      'Dream Coins media economy',
      'Tiered chat memory',
    ],
  },
  {
    slug: 'aigirlfriends-ai',
    reviewSlug: 'aigirlfriends-ai-review',
    productName: 'AIGirlfriends.ai',
    logoLabel: 'AG',
    image: `/new-ai-platforms/aigirlfriends-ai-${aug2026PlatformImageVersion}.webp`,
    category: 'All-in-One AI Girlfriend',
    readTime: '7 min read',
    platformUrl: '/go/aigirlfriends-ai',
    platformCtaLabel: 'Visit AIGirlfriends.ai',
    featured: false,
    dateLabel: 'Aug 2026 tracker',
    signalScore: 4.34,
    statusLabel: 'New scan',
    price: 'Free tier; paid plans reported from $9.99 to $12.99/month',
    description:
      'AIGirlfriends.ai is a browser-based AI girlfriend platform where users design one persistent companion and interact through unlimited chat, AI images, video, voice messages, and live voice calls under a flat monthly plan. Its social-feed UI, 11-step character builder, and cross-feature identity consistency make it a strong pick for users who want a single recognizable virtual partner.',
    bestFor:
      'Readers who want one consistent AI girlfriend across chat, photos, video, voice notes, and live calls without juggling separate credit systems for each feature.',
    reviewTitle: 'AIGirlfriends.ai Review: One Companion, Chat, Images, Video, and Voice Calls',
    reviewExcerpt:
      'AIGirlfriends.ai offers a flat-rate AI girlfriend experience with a guided builder, social feed, unlimited chat and images, voice messages, live calls, and persistent companion memory.',
    reviewSummary:
      'AIGirlfriends.ai belongs in this batch because it solves a common AI girlfriend problem: the same character looking different in chat, images, and voice. The 11-step builder, companion-consistent image generation, social timeline, and bundled voice call allowance create a complete relationship-style product in one subscription. Heaven Girlfriend should verify active promotional pricing, test memory depth over multiple weeks, and compare whether 30 minutes of monthly voice calls is enough for power users.',
    standoutPoints: [
      'One persistent AI girlfriend stays visually and tonally consistent across chat, images, video, and voice.',
      'Flat monthly pricing bundles unlimited chat and images with voice call and voice message allowances.',
      'Social-feed timeline adds an app-like layer between live chat sessions.',
      '11-step guided builder covers appearance, personality, backstory, and voice profile selection.',
    ],
    watchPoints: [
      'Headline pricing varies between $9.99 and $12.99 depending on active promotions.',
      'No native mobile app; browser access only on iOS and Android.',
      'Voice call minutes beyond the monthly allowance may require token or credit add-ons.',
      'Memory depth is not fully documented publicly and needs multi-session testing.',
    ],
    features: [
      '11-step AI girlfriend builder',
      'Unlimited chat and image generation',
      'Live AI voice calls',
      'Voice messages',
      'AI video generation',
      'Social companion feed',
      'Persistent memory',
    ],
  },
  {
    slug: 'mybabes-ai',
    reviewSlug: 'mybabes-ai-review',
    productName: 'MyBabes.ai',
    logoLabel: 'MB',
    image: `/new-ai-platforms/mybabes-ai-${aug2026PlatformImageVersion}.webp`,
    category: 'Visual AI Girlfriend Platform',
    readTime: '7 min read',
    platformUrl: '/go/mybabes-ai',
    platformCtaLabel: 'Visit MyBabes.ai',
    featured: false,
    dateLabel: 'Aug 2026 tracker',
    signalScore: 4.3,
    statusLabel: 'New scan',
    price: 'Free tier; Premium from $14.99 to $34.99/month plus token packs',
    description:
      'MyBabes.ai is an adult AI girlfriend and companion platform from Memora AI s.r.o. (Prague) combining roleplay chat, 2K image generation, short-form AI video, and a TikTok-style Reels feed. Its Visual Identity Anchor, dual chat modes, and Impossible Difficulty setting give users unusually responsive, personality-driven virtual relationships in the browser.',
    bestFor:
      'Users who want visually consistent AI girlfriends with 2K images, short AI video, community character discovery, and roleplay that can push back instead of always agreeing.',
    reviewTitle: 'MyBabes.ai Review: 2K Images, AI Video, Reels, and Impossible Difficulty Mode',
    reviewExcerpt:
      'MyBabes.ai is an NSFW AI girlfriend platform with 2K image and video generation, persistent character memory, dual chat modes, Reels discovery, and an Impossible Difficulty interaction setting.',
    reviewSummary:
      'MyBabes.ai earns a strong signal score because it is more than a chat wrapper: the Visual Identity Anchor keeps characters consistent across chat, images, and video, while Impossible Difficulty adds a rare dynamic where AI companions respond to user energy instead of blindly agreeing. With 1.4M+ registered users since a late-2024 launch, it is a legitimate rising platform. Heaven Girlfriend should test token burn rates on video, verify privacy documentation, and confirm whether the dense interface suits casual first-time users.',
    standoutPoints: [
      'Visual Identity Anchor maintains character consistency across chat, 2K images, and short AI video.',
      'Impossible Difficulty mode creates more realistic pushback and engagement dynamics.',
      'Reels feed and community Explore gallery offer discovery beyond one-on-one companion chat.',
      'Dual AI model switching supports emotional versus descriptive conversation styles mid-chat.',
    ],
    watchPoints: [
      'Web-only with no dedicated mobile app in 2026.',
      'Video generation consumes tokens quickly on top of subscription tiers.',
      'Public privacy documentation is thinner than category leaders like Candy AI.',
      'Premium pricing up to $34.99/month plus token packs can escalate for heavy media users.',
    ],
    features: [
      'AI girlfriend roleplay chat',
      '2K AI image generation',
      'Short-form AI video',
      'Visual Identity Anchor',
      'Impossible Difficulty mode',
      'Reels discovery feed',
      'Community character gallery',
    ],
  },
  {
    slug: 'infatuated-ai',
    reviewSlug: 'infatuated-ai-review',
    productName: 'Infatuated AI',
    logoLabel: 'IN',
    image: `/new-ai-platforms/infatuated-ai-${aug2026PlatformImageVersion}.webp`,
    category: 'Multimodal AI Girlfriend',
    readTime: '7 min read',
    platformUrl: '/go/infatuated-ai',
    platformCtaLabel: 'Visit Infatuated AI',
    featured: false,
    dateLabel: 'Aug 2026 tracker',
    signalScore: 4.26,
    statusLabel: 'New scan',
    price: 'Free tier; paid plans from $5.99/mo (annual) to about $14.87/mo with monthly tokens',
    description:
      'Infatuated AI is a privacy-focused AI girlfriend platform at infatuated.ai with 100+ pre-built companions, deep customization, encrypted chat, in-chat selfies, voice messages, and AI video clips. Launched in late 2024, it targets adult users who want one platform for romantic chat, NSFW roleplay, and generated media without switching apps.',
    bestFor:
      'Users who want a multimodal AI girlfriend with chat, images, voice, and video in one encrypted environment, especially at the lower annual price point.',
    reviewTitle: 'Infatuated AI Review: Chat, Selfies, Voice, Video, and Token Pricing',
    reviewExcerpt:
      'Infatuated AI combines customizable AI girlfriends, adaptive memory, in-chat image requests, voice messages, AI video, and token-based premium access on infatuated.ai.',
    reviewSummary:
      'Infatuated AI fits this batch as a legitimate multimodal AI girlfriend product with clear 18+ positioning, 100+ distinct pre-built characters, and integrated media generation inside the chat flow. It has grown quickly since late 2024 and appeals to privacy-conscious users in the US, UK, and EU. Heaven Girlfriend should verify token consumption per image and video, test memory beyond short sessions, and note that the platform is English-only despite global traffic.',
    standoutPoints: [
      'Chat, in-chat selfies, voice messages, and AI video clips live in one companion interface.',
      '100+ pre-built characters with distinct personalities reduce cold-start friction.',
      'Privacy-first messaging with encrypted data and private gallery options.',
      'Annual plans from $5.99/month undercut many premium AI girlfriend competitors.',
    ],
    watchPoints: [
      'Token-based media economy can add cost beyond the subscription headline.',
      'English-only support may limit non-English speaking users globally.',
      'Memory depth should be tested over longer relationship-style sessions.',
      'End-to-end encryption claims deserve verification against the live privacy policy.',
    ],
    features: [
      'Custom AI girlfriend builder',
      '100+ pre-built companions',
      'In-chat AI selfies',
      'Voice messages',
      'AI video generation',
      'Adaptive conversation memory',
      'Encrypted private gallery',
    ],
  },
  {
    slug: 'bonza-chat',
    reviewSlug: 'bonza-chat-review',
    productName: 'Bonza.Chat',
    logoLabel: 'BZ',
    image: `/new-ai-platforms/bonza-chat-${aug2026PlatformImageVersion}.webp`,
    category: 'AI Girlfriend Chat Platform',
    readTime: '6 min read',
    platformUrl: '/go/bonza-chat',
    platformCtaLabel: 'Visit Bonza.Chat',
    featured: false,
    dateLabel: 'Aug 2026 tracker',
    signalScore: 4.24,
    statusLabel: 'New scan',
    price: 'Free tier; Premium from $3.99/mo (annual) to $12.99/mo with 100 monthly tokens',
    description:
      'Bonza.Chat is a New York-based AI girlfriend platform where users browse 100+ ready-made companions or build custom AI girlfriends with unique personalities, voices, and interests. The uncensored chat engine remembers relationship history, supports NSFW roleplay, and includes custom AI image generation through a browser-based PWA with discreet billing.',
    bestFor:
      'Users in the US and globally who want affordable unlimited AI girlfriend chat, custom character creation, NSFW freedom, and in-chat image generation starting under $4/month on annual billing.',
    reviewTitle: 'Bonza.Chat Review: Custom Characters, Memory, and Adult Chat',
    reviewExcerpt:
      'Bonza.Chat is an AI girlfriend platform with 100+ companions, custom creation, contextual memory, NSFW chat, AI image generation, and Premium plans from $3.99/month.',
    reviewSummary:
      'Bonza.Chat belongs on the tracker because it hits core AI girlfriend search intent directly: create or choose a virtual partner, flirt without filters, and build a relationship that remembers prior chats. Premium at $3.99/month annually is aggressively priced for unlimited messaging plus 100 monthly tokens. Heaven Girlfriend should test image token burn, voice call availability if promoted, and whether free-tier daily limits still give enough room for a meaningful first-look review.',
    standoutPoints: [
      '100+ ready-made AI girlfriends plus full custom companion creation on Premium.',
      'Contextual memory tracks preferences, inside jokes, and relationship history.',
      'Uncensored NSFW chat without the restrictive filters found on mainstream AI apps.',
      'Annual Premium under $4/month with discreet billing suits privacy-conscious US and global users.',
    ],
    watchPoints: [
      'English is the primary supported language today.',
      'Free tier daily message limits may restrict extended first-session testing.',
      'Token costs for image generation beyond the 100 monthly allowance need documentation.',
      'Refund window is limited to 24 hours with low token usage per official FAQ.',
    ],
    features: [
      '100+ AI girlfriend characters',
      'Custom companion builder',
      'Uncensored NSFW chat',
      'Contextual memory',
      'AI image generation',
      'Browser PWA access',
      'Discreet billing',
    ],
  },
  {
    slug: 'funfun-ai',
    reviewSlug: 'funfun-ai-review',
    productName: 'FunFun AI',
    logoLabel: 'FF',
    image: `/new-ai-platforms/funfun-ai-${aug2026PlatformImageVersion}.webp`,
    category: 'Emotional AI Girlfriend',
    readTime: '6 min read',
    platformUrl: '/go/funfun-ai',
    platformCtaLabel: 'Visit FunFun AI',
    featured: false,
    dateLabel: 'Aug 2026 tracker',
    signalScore: 4.22,
    statusLabel: 'New scan',
    price: '',
    description:
      'FunFun AI is a web-based AI girlfriend platform at funfun.ai with 337+ pre-built companions, a seven-step custom builder, persistent memory, in-chat photo requests, voice replies, and multilingual chat across 16 languages. It balances emotional realism and mild NSFW flirting for users who want grounded virtual relationships rather than pure fantasy overload.',
    bestFor:
      'Users who want emotionally aware AI girlfriend chat, consistent in-chat selfies, multicultural character variety, and multilingual support across Europe and global markets.',
    reviewTitle: 'FunFun AI Review: 337+ AI Girlfriends, Memory, Photos, and Voice Replies',
    reviewExcerpt:
      'FunFun AI offers 337+ AI girlfriends, a seven-step builder, persistent memory, in-chat photo requests, voice replies, and 16-language chat on funfun.ai.',
    reviewSummary:
      'FunFun AI is a legitimate companion-first AI girlfriend website that prioritizes conversational continuity and character realism over shock-value NSFW marketing. With 337+ diverse pre-built companions, GDPR-aligned privacy messaging, and 16-language support, it has clear appeal for European and international users. Heaven Girlfriend should verify premium pricing at checkout, test image generation latency, and confirm how far NSFW boundaries extend compared with fully uncensored platforms.',
    standoutPoints: [
      '337+ pre-built characters with a wide range of looks and personality types.',
      'Seven-step builder gives structured control over appearance, style, and personality.',
      'Persistent memory and mood-aware replies support longer virtual relationships.',
      '16-language chat support makes it one of the more GEO-friendly AI girlfriend platforms.',
    ],
    watchPoints: [
      'NSFW is milder than fully uncensored competitors; adult users should set expectations accordingly.',
      'Image generation can take several seconds and may affect chat flow.',
      'Premium pricing should be confirmed at checkout rather than third-party listings.',
      'Voice replies and unlimited messaging appear tied to paid tiers.',
    ],
    features: [
      '337+ AI girlfriend characters',
      'Seven-step custom builder',
      'Persistent companion memory',
      'In-chat photo requests',
      'Voice replies',
      '16-language chat',
      'GDPR-aligned privacy',
    ],
  },
  {
    slug: 'lustgf',
    reviewSlug: 'lustgf-review',
    productName: 'LustGF',
    logoLabel: 'LG',
    image: `/new-ai-platforms/lustgf-${aug2026PlatformImageVersion}.webp`,
    category: 'Budget NSFW AI Girlfriend',
    readTime: '6 min read',
    platformUrl: '/go/lustgf',
    platformCtaLabel: 'Visit LustGF',
    featured: false,
    dateLabel: 'Aug 2026 tracker',
    signalScore: 4.2,
    statusLabel: 'New scan',
    price: 'Free tier; from $5.99/mo (annual) to $12.99/mo with 30-day refund policy',
    description:
      'LustGF is an uncensored AI girlfriend platform at lustgf.com with 10,000+ pre-built characters, deep appearance and personality customization, encrypted messaging, AI image generation, and Telegram bot access. Its annual plan under $6/month makes it one of the most affordable NSFW virtual companion options for privacy-minded adult users.',
    bestFor:
      'Budget-conscious users who want uncensored AI girlfriend chat, huge character variety, image generation, and Telegram access without premium-tier pricing.',
    reviewTitle: 'LustGF Review: 10,000+ Characters, Adult Chat, and Telegram Integration',
    reviewExcerpt:
      'LustGF is a low-cost NSFW AI girlfriend platform with 10,000+ characters, deep customization, encrypted chat, image generation, and Telegram bot support.',
    reviewSummary:
      'LustGF is a legitimate adult AI girlfriend product launched in late 2023 with a massive curated character library and strong privacy positioning. It undercuts many competitors on annual pricing while supporting adult (18+) chat and solid image output for the category. Heaven Girlfriend should note the lack of voice chat and native app, test Telegram bot quality versus the web experience, and verify whether the 10,000+ library quality is consistent or front-loaded with duplicates.',
    standoutPoints: [
      '10,000+ AI girlfriend characters cover diverse niches, kinks, and conversation styles.',
      'Annual pricing from $5.99/month is among the lowest in the NSFW AI companion space.',
      'Encrypted messaging and Telegram integration suit privacy-focused users.',
      '30-day refund policy offers more buyer protection than many adult AI platforms.',
    ],
    watchPoints: [
      'No dedicated mobile app; Telegram partially fills the mobile gap.',
      'No voice chat or voice message support at time of review.',
      'Image generation is locked behind paid tiers with no free-tier access.',
      'Character library size may not equal consistent conversation quality across all profiles.',
    ],
    features: [
      '10,000+ AI girlfriend characters',
      'Deep character customization',
      'Uncensored NSFW chat',
      'AI image generation',
      'Encrypted messaging',
      'Telegram bot access',
      'Persistent memory',
    ],
  },
  {
    slug: 'heyreal-ai',
    reviewSlug: 'heyreal-ai-review',
    productName: 'HeyReal AI',
    logoLabel: 'HR',
    image: `/new-ai-platforms/heyreal-ai-${aug2026PlatformImageVersion}.webp`,
    category: 'Text-First AI Girlfriend',
    readTime: '6 min read',
    platformUrl: '/go/heyreal-ai',
    platformCtaLabel: 'Visit HeyReal AI',
    featured: false,
    dateLabel: 'Aug 2026 tracker',
    signalScore: 4.16,
    statusLabel: 'New scan',
    price: 'Free tier; Standard from $3.90/mo (annual) to $12.99/mo; Premium up to $49.99/mo',
    description:
      'HeyReal.ai is a browser-based AI girlfriend and companion platform focused on emotionally intelligent text chat, deep character customization, and unfiltered NSFW roleplay. Users build anime or realistic AI girlfriends with detailed personalities and backstories, switching between SFW and mature modes without leaving the same companion profile.',
    bestFor:
      'Readers who prioritize emotionally engaging text-based AI girlfriend chat and roleplay over image, video, or voice-heavy companion apps.',
    reviewTitle: 'HeyReal AI Review: Custom Characters, Adult Chat, and Emotional Roleplay',
    reviewExcerpt:
      'HeyReal AI offers customizable AI girlfriends, unfiltered NSFW and SFW chat modes, emotional roleplay, and tiered Premium plans from $3.90/month on heyreal.ai.',
    reviewSummary:
      'HeyReal AI is a legitimate text-first AI girlfriend platform with strong customization and unfiltered conversation options at competitive annual pricing. It fits users who want connection and roleplay depth without paying for heavy media generation. Heaven Girlfriend should test memory consistency across sessions, clarify conflicting reports about voice features, and compare Deluxe tier value against simpler Standard plans.',
    standoutPoints: [
      'Deep character builder covers nickname, gender, personality, backstory, and avatar style.',
      'Unfiltered NSFW and SFW modes on the same companion profile support flexible use cases.',
      'Annual Standard plan from $3.90/month is affordable for unlimited messaging tiers.',
      'Emotionally responsive dialogue suits slow-burn romance and immersive roleplay scenarios.',
    ],
    watchPoints: [
      'No native mobile app; browser-only access on phone and desktop.',
      'Limited in-chat image generation compared with visual-first AI girlfriend platforms.',
      'Character memory performance may vary between free and Premium tiers.',
      'Top Deluxe tier at $49.99/month needs clear feature justification versus Standard.',
    ],
    features: [
      'Custom AI girlfriend builder',
      'Anime and realistic character styles',
      'Unfiltered NSFW chat',
      'SFW companion mode',
      'Emotional roleplay',
      'Pre-built character library',
      'Tiered subscription plans',
    ],
  },
  {
    slug: 'virtualcrush',
    reviewSlug: 'virtualcrush-review',
    productName: 'VirtualCrush',
    logoLabel: 'VC',
    image: `/new-ai-platforms/virtualcrush-${aug2026PlatformImageVersion}.webp`,
    category: 'Adaptive AI Girlfriend',
    readTime: '6 min read',
    platformUrl: '/go/virtualcrush',
    platformCtaLabel: 'Visit VirtualCrush',
    featured: false,
    dateLabel: 'Aug 2026 tracker',
    signalScore: 4.14,
    statusLabel: 'New scan',
    price: 'Free tier; premium subscription pricing shown at checkout',
    description:
      'VirtualCrush is an adult AI girlfriend platform at virtualcrush.ai built for real-time, adaptive chat with customizable digital companions. Users create partners with adjustable tone, personality, and interaction style, then engage in private uncensored conversations with context-aware memory and no public profile exposure.',
    bestFor:
      'Adult users who want a private, browser-based AI girlfriend focused on immersive text roleplay, real-time responsiveness, and full companion customization.',
    reviewTitle: 'VirtualCrush Review: Real-Time AI Girlfriend Chat and Private Custom Companions',
    reviewExcerpt:
      'VirtualCrush offers customizable AI girlfriends, real-time adaptive chat, uncensored private sessions, and context-aware memory on virtualcrush.ai.',
    reviewSummary:
      'VirtualCrush is a legitimate adult AI companion site with clear RTA labeling, transparent legal pages, and a privacy-first session model where interactions stay private with no visible activity feed. It targets users who want girlfriend-style fantasy chat rather than therapy or professional support. Heaven Girlfriend should document exact premium pricing at checkout, test scenario variety, and verify how strongly memory persists across return visits.',
    standoutPoints: [
      'Real-time adaptive responses keep conversations flowing without awkward delays.',
      'Fully private sessions with no public profile or activity feed.',
      'Uncensored interaction supports adult roleplay without rigid content walls.',
      'Custom tone, personality, and interaction style controls on each companion.',
    ],
    watchPoints: [
      'Premium pricing is not fully transparent before account creation.',
      'Primarily English by default; limited multilingual support.',
      'Advanced features sit behind the paywall after the free tier.',
      'Not designed for users seeking voice, video, or heavy image generation.',
    ],
    features: [
      'Custom AI companion builder',
      'Real-time adaptive chat',
      'Uncensored private sessions',
      'Context-aware memory',
      'Pre-built scenario options',
      'Browser-based access',
      'Adult-only RTA labeling',
    ],
  },
  {
    slug: 'justmet',
    reviewSlug: 'justmet-review',
    productName: 'JustMet',
    logoLabel: 'JM',
    image: `/new-ai-platforms/justmet-${aug2026PlatformImageVersion}.webp`,
    category: 'Mobile AI Girlfriend App',
    readTime: '6 min read',
    platformUrl: '/go/justmet',
    platformCtaLabel: 'Get JustMet App',
    featured: false,
    dateLabel: 'Aug 2026 tracker',
    signalScore: 4.1,
    statusLabel: 'New scan',
    price: 'Free start; subscriptions from $3.99/week to $44.99/year plus gem unlocks',
    description:
      'JustMet is a mobile-first AI girlfriend app by IsetAI available on iOS and Android with polyamorous companion support, affection-trust-passion progression, custom character creation, and in-app photo and video unlocks. Built for AI dating sim and roleplay fans, it targets users who want relationship stats and multiple AI girlfriends in one native app.',
    bestFor:
      'Mobile users who prefer a native AI girlfriend app with poly romance mechanics, relationship progression stats, and anime-style character roleplay over browser-based companion sites.',
    reviewTitle: 'JustMet Review: Poly AI Girlfriends, Relationship Stats, and Mobile Roleplay',
    reviewExcerpt:
      'JustMet is a mobile AI girlfriend app with poly companion support, affection and trust progression, custom characters, and gem-based photo unlocks on iOS and Android.',
    reviewSummary:
      'JustMet is a legitimate app-store AI girlfriend product with no standalone marketing site, which makes it distinct from the browser platforms in this batch. Its poly AI angle, relationship progression mechanics, and native iOS/Android apps suit mobile-first users in the US, India, and other App Store markets. Heaven Girlfriend should flag gem costs for photo unlocks, mixed Google Play reliability reports, and the 18+ entertainment-only positioning before recommending paid tiers.',
    standoutPoints: [
      'Native iOS and Android apps with no browser workaround required.',
      'Poly AI support lets users maintain multiple girlfriend storylines simultaneously.',
      'Affection, trust, and passion stats create dating-sim style relationship progression.',
      'Custom AI character creation with anime, game, and real-world personality templates.',
    ],
    watchPoints: [
      'No dedicated marketing website; app store listings are the primary product surface.',
      'Gem-based photo and video unlocks can inflate costs beyond subscription pricing.',
      'Mixed user reports about characters stopping replies after early progression levels.',
      'Subscription plus in-app gem purchases require careful total cost tracking.',
    ],
    features: [
      'Native iOS and Android apps',
      'Poly AI girlfriend support',
      'Relationship progression stats',
      'Custom character creation',
      'AI roleplay chat',
      'In-app photo and video unlocks',
      'Anime and game-inspired companions',
    ],
  },
];

export const nextWeekNewPlatformQueue: NewPlatformTrackerItem[] = [
  {
    slug: 'sweetdream-ai',
    reviewSlug: 'sweetdream-ai-review',
    productName: 'SweetDream AI',
    logoLabel: 'SD',
    image: '/new-ai-platforms/dreamle.webp',
    category: 'NSFW AI Girlfriend Builder',
    readTime: '6 min read',
    platformUrl: '/go/sweetdream-ai',
    platformCtaLabel: 'Visit SweetDream AI',
    featured: false,
    dateLabel: 'Next week queue',
    signalScore: 4.28,
    statusLabel: 'Queued',
    price: '',
    description:
      'Adult AI girlfriend builder with custom face, body, voice, personality, uncensored chat, generated images and videos, voice messages, and promoted call-style interactions.',
    bestFor:
      'Users who want a more explicit multimodal AI girlfriend experience with visual and call-style features.',
    reviewTitle: 'SweetDream AI Review: Are Its Live Calls Actually Live?',
    reviewExcerpt:
      'SweetDream AI promotes custom AI girlfriends, voice messages, images, videos, persistent memory, and call-style interactions for adults.',
    reviewSummary:
      'SweetDream AI is queued because it has the right feature set for a strong Heaven Girlfriend review, but the public messaging around "live" interaction needs scrutiny. The product appears to combine builder controls, adult chat, images, videos, voice messages, and memory. That is enough for a detailed review if the experience is real-time and consistent. The main investigative angle is whether calls behave like live generative sessions, rendered clips, or simply marketing language.',
    standoutPoints: [
      'Custom face, body, voice, and personality controls match high-intent AI girlfriend users.',
      'Images, videos, and voice messages support a complete companion experience.',
      'Adult-first positioning fits mature companion comparisons.',
    ],
    watchPoints: [
      'Live call claims need direct timing and interaction testing.',
      'Token or subscription limits should be documented before value scoring.',
      'Trust and pricing details need confirmation from checkout rather than listings.',
    ],
    features: [
      'Custom AI girlfriend builder',
      'Uncensored chat',
      'Voice messages',
      'AI images and videos',
      'Memory',
      'Call-style interactions',
    ],
  },
  {
    slug: 'xomi',
    reviewSlug: 'xomi-review',
    productName: 'XOMI',
    logoLabel: 'XO',
    image: '/new-ai-platforms/couple-me.webp',
    category: 'Interactive Fiction Companion',
    readTime: '6 min read',
    platformUrl: '/go/xomi',
    platformCtaLabel: 'Visit XOMI',
    featured: false,
    dateLabel: 'Next week queue',
    signalScore: 4.18,
    statusLabel: 'Queued',
    price: 'Free daily messages, Pro plan reported around $7.99/month',
    description:
      'Interactive fiction and dating-sim-adjacent companion platform where story characters can become persistent companions after branching chapters.',
    bestFor:
      'Readers who want romance, roleplay, and companion chat to start from story chapters instead of profile browsing.',
    reviewTitle: 'XOMI Review: When Dating-Sim Characters Become Permanent AI Companions',
    reviewExcerpt:
      'XOMI blends branching AI stories, persistent companions, affinity systems, memory, custom characters, free-form roleplay, and multilingual support.',
    reviewSummary:
      'XOMI is prepared for next week because it offers a differentiated path into AI companionship: story first, relationship second. If the transition from interactive fiction into persistent companion chat works, it could appeal to readers who find ordinary AI girlfriend directories too static. The review should test whether affinity, story choices, and memory actually affect post-story behavior. It is a weaker pure AI girlfriend fit than Dondi or Kissable, but stronger as a dating-sim and relationship-story experiment.',
    standoutPoints: [
      'Story-to-companion flow is more original than standard companion browsing.',
      'Affinity and memory systems create clear mechanics to test.',
      'Free daily messages make the first-look review easier for readers to reproduce.',
    ],
    watchPoints: [
      'Romance quality may vary heavily by story and character.',
      'It is not primarily marketed as an AI girlfriend app.',
      '"Unlimited" language should be checked against daily message caps.',
    ],
    features: [
      'Branching AI story chapters',
      'Persistent post-story companions',
      'Affinity system',
      'Memory',
      'Custom character creation',
      'Free-form roleplay',
      'Multilingual support',
    ],
  },
];

export const lastWeekNewPlatformTrackerItems: NewPlatformTrackerItem[] = [
  {
    slug: 'reverie',
    reviewSlug: 'reverie-review',
    productName: 'Reverie',
    logoLabel: 'RV',
    image: '/new-ai-platforms/reverie.webp',
    category: 'Long-Term AI Companion',
    readTime: '6 min read',
    platformUrl: '/go/reverie',
    platformCtaLabel: 'Visit Reverie',
    featured: true,
    dateLabel: 'Jul 2026 batch',
    signalScore: 4.35,
    statusLabel: 'New scan',
    price: '',
    description:
      'Reverie is an AI companion platform built around persistent relationships rather than one-off chatbot conversations. Users can create customizable AI companions with long-term memory, voice calls, proactive messaging, group chats, and interactive storytelling, making it a strong choice for users seeking immersive AI companionship and relationship-focused experiences.',
    bestFor:
      'Readers who want AI companions built for long-term memory, proactive messaging, voice calls, and relationship continuity instead of disposable chat sessions.',
    reviewTitle: 'Reverie Review: Persistent Memory, Voice Calls, and Long-Term AI Relationships',
    reviewExcerpt:
      'Reverie is an AI companion platform built around persistent relationships with customizable companions, long-term memory, voice calls, proactive messaging, group chats, and interactive storytelling.',
    reviewSummary:
      'Reverie is built around persistent relationships rather than one-off chatbot conversations. Users can create customizable AI companions with long-term memory, voice calls, proactive messaging, group chats, and interactive storytelling, which gives Heaven Girlfriend a strong editorial angle around continuity and immersive companionship. The review should test whether memory and proactive messaging hold up over multiple days, whether group chats feel coherent, and whether voice and storytelling features add real relationship depth or mostly serve as marketing language.',
    standoutPoints: [
      'Persistent relationships and long-term memory fit ongoing AI companionship use cases.',
      'Voice calls, proactive messaging, and group chats add depth beyond text-only apps.',
      'Interactive storytelling supports immersive relationship-focused experiences.',
      'Customizable companions give users control over personality and relationship framing.',
    ],
    watchPoints: [
      'Memory and proactive messaging should be tested over multiple sessions.',
      'Group chat quality and coherence need hands-on verification.',
      'Pricing, free limits, and subscription tiers require checkout confirmation.',
      'Privacy and data retention policies should be reviewed before strong recommendation.',
    ],
    features: [
      'Long-term companion memory',
      'Customizable AI companions',
      'Voice calls',
      'Proactive messaging',
      'Group chats',
      'Interactive storytelling',
    ],
  },
  {
    slug: 'xeve-ai',
    reviewSlug: 'xeve-ai-review',
    productName: 'Xeve AI',
    logoLabel: 'XE',
    image: '/new-ai-platforms/xeve-ai.webp',
    category: 'Video-First AI Girlfriend',
    readTime: '6 min read',
    platformUrl: '/go/xeve-ai',
    platformCtaLabel: 'Visit Xeve AI',
    featured: false,
    dateLabel: 'Jul 2026 batch',
    signalScore: 4.3,
    statusLabel: 'New scan',
    price: '',
    description:
      'Xeve AI is an AI companion platform that combines AI girlfriend chat, live AI video conversations, customizable characters, and AI-generated media in a single browser-based experience. Designed for interactive virtual relationships, it supports text chat, video interactions, character creation, and multimedia content generation for personalized AI companionship.',
    bestFor:
      'Readers who want live AI video conversations, character creation, and generated media in one browser-based companion experience.',
    reviewTitle: 'Xeve AI Review: Live Video Chat, AI Girlfriends, and Generated Media',
    reviewExcerpt:
      'Xeve AI combines AI girlfriend chat, live AI video conversations, customizable characters, and AI-generated media for interactive virtual relationships in one browser-based experience.',
    reviewSummary:
      'Xeve AI belongs in this live batch because it combines AI girlfriend chat, live AI video conversations, customizable characters, and AI-generated media in a single browser-based experience designed for interactive virtual relationships. That gives Heaven Girlfriend enough feature depth for a meaningful first-look review across text chat, video interactions, character creation, and multimedia generation. The key investigative work is separating marketing language from actual interaction quality: how responsive video feels, whether character identity stays consistent, and how much of the experience requires paid access.',
    standoutPoints: [
      'Live AI video conversations give it a distinct angle among new companion platforms.',
      'Combines text chat, video, character creation, and generated media in one flow.',
      'Browser-based access lowers friction for first-look testing.',
      'Interactive virtual relationship positioning fits Heaven Girlfriend reader intent.',
    ],
    watchPoints: [
      'Live video responsiveness and realism need direct timing tests.',
      'Media generation limits and credit costs should be documented at checkout.',
      'Character consistency across video and text sessions may vary.',
      'Free and paid feature boundaries are not published clearly enough to judge from the marketing page.',
    ],
    features: [
      'AI girlfriend chat',
      'Live AI video conversations',
      'Customizable characters',
      'AI-generated media',
      'Text chat',
      'Character creation',
    ],
  },
  {
    slug: 'xchar',
    reviewSlug: 'xchar-review',
    productName: 'XCHAR',
    logoLabel: 'XC',
    image: '/new-ai-platforms/xchar.webp',
    category: 'AI Girlfriend Platform',
    readTime: '6 min read',
    platformUrl: '/go/xchar',
    platformCtaLabel: 'Visit XCHAR',
    featured: false,
    dateLabel: 'Jul 2026 batch',
    signalScore: 4.15,
    statusLabel: 'New scan',
    price: '',
    description:
      'XCHAR is an AI girlfriend and character platform that allows users to create, customize, and chat with virtual companions across roleplay and relationship-focused scenarios. Its emphasis on flexible character creation and open-ended conversations makes it suitable for users looking for personalized AI companion experiences.',
    bestFor:
      'Users who want flexible AI girlfriend and character creation with open-ended roleplay and relationship-focused conversations.',
    reviewTitle: 'XCHAR Review: AI Girlfriend Creation, Roleplay, and Open-Ended Companion Chat',
    reviewExcerpt:
      'XCHAR is an AI girlfriend and character platform for creating, customizing, and chatting with virtual companions across roleplay and relationship-focused scenarios.',
    reviewSummary:
      'XCHAR fits this batch because it combines AI girlfriend positioning with flexible character creation and open-ended conversations across roleplay and relationship-focused scenarios. That makes it relevant for readers who want personalized AI companion experiences without a rigid onboarding funnel. The review should test character consistency, how well customization carries into conversation quality, moderation boundaries, and whether romantic companion setups feel personal or generic.',
    standoutPoints: [
      'AI girlfriend and character creation support personalized companion setups.',
      'Flexible character creation suits users who want control over companion design.',
      'Open-ended conversations work for roleplay and relationship-focused scenarios.',
      'Relationship-focused use cases fit Heaven Girlfriend companion comparisons.',
    ],
    watchPoints: [
      'Quality may vary depending on character setup and conversation style.',
      'Open-ended roleplay requires careful privacy and moderation review.',
      'Pricing and premium feature gates should be verified before value scoring.',
      'Visual, voice, and media features should be documented during testing.',
    ],
    features: [
      'AI girlfriend and character creation',
      'Customizable virtual companions',
      'Roleplay scenarios',
      'Relationship-focused conversations',
      'Open-ended companion chat',
    ],
  },
  {
    slug: 'joi-ai',
    reviewSlug: 'joi-ai-review',
    productName: 'Joi AI',
    logoLabel: 'JI',
    image: '/new-ai-platforms/joi-ai.webp',
    category: 'AI Companion Platform',
    readTime: '6 min read',
    platformUrl: '/go/joi-ai',
    platformCtaLabel: 'Visit Joi AI',
    featured: false,
    dateLabel: 'Jul 2026 batch',
    signalScore: 3,
    statusLabel: 'Tested',
    price: '$15 for 1,000 neurons',
    description:
      'Joi AI is a character-chat platform at joi.ai with a simple interface, personality-driven replies, typing indicators, and a preset gallery unlocked with neurons.',
    bestFor:
      'Readers who want casual AI girlfriend chat with several fictional personalities and a distraction-free interface.',
    reviewTitle: 'Joi AI Review: Clean Character Chat, Weak Gallery',
    reviewExcerpt:
      'Joi AI offers character-based AI girlfriend chat with a clean interface. The preset gallery is low quality and some images cost thousands of neurons.',
    reviewSummary:
      'Joi AI is a character-chat platform. A hands-on test with Vera, a 21-year-old K-pop-inspired character, found a clean interface, personality-driven replies, typing indicators, and follow-up messages. The preset gallery does not take custom prompts. Unlocked images looked low quality, and some cost 300 neurons while others cost about 2,500 to 4,750 against a 1,000-neuron subscription.',
    standoutPoints: [
      'The chat screen stays simple and keeps the character in focus.',
      'Vera held a confident K-pop persona through the conversation.',
      'Typing indicators and follow-up messages make the chat feel more active.',
      'The roster covers more than one character style.',
    ],
    watchPoints: [
      'In-character terms such as Flair, civilian, and fandom name were not explained.',
      'Some replies sound scripted.',
      'There is no custom image generation. Gallery images are pre-made.',
      'A $15 plan includes 1,000 neurons, and some images cost 2,500 to 4,750 neurons.',
    ],
    features: [
      'Character selection',
      'Text chat',
      'Personality profiles',
      'Typing indicators',
      'Follow-up messages',
      'Preset image gallery',
      'Neuron image unlocks',
      'Character video introductions',
    ],
  },
  {
    slug: 'virtualover',
    reviewSlug: 'virtualover-review',
    productName: 'VirtuaLover',
    logoLabel: 'VL',
    image: '/new-ai-platforms/virtualover.webp',
    category: 'Virtual Relationship AI',
    readTime: '6 min read',
    platformUrl: '/go/virtualover',
    platformCtaLabel: 'Visit VirtuaLover',
    featured: false,
    dateLabel: 'Jul 2026 batch',
    signalScore: 4.18,
    statusLabel: 'New scan',
    price: '',
    description:
      'VirtuaLover is an AI companion platform designed around virtual relationships, personalized conversations, and customizable AI characters. It aims to provide users with immersive AI companionship through relationship-focused interactions and interactive character experiences.',
    bestFor:
      'Users who want virtual relationships, personalized conversations, and customizable AI characters in an immersive companion experience.',
    reviewTitle: 'VirtuaLover Review: Virtual Relationships, Custom Characters, and Immersive Chat',
    reviewExcerpt:
      'VirtuaLover is an AI companion platform built around virtual relationships, personalized conversations, customizable AI characters, and relationship-focused interactions.',
    reviewSummary:
      'VirtuaLover fits this batch because it is designed around virtual relationships, personalized conversations, and customizable AI characters with relationship-focused interactions and interactive character experiences. That gives Heaven Girlfriend a useful framework for testing immersive AI companionship rather than one-off roleplay exchanges. The review should verify differentiation from similar relationship-focused apps and document visual, voice, or media features that affect overall value.',
    standoutPoints: [
      'Virtual relationship framing fits Heaven Girlfriend companion comparisons.',
      'Personalized conversations and customizable characters support immersive setups.',
      'Relationship-focused interactions create a clear quality-testing angle.',
      'Interactive character experiences go beyond basic chatbot behavior.',
    ],
    watchPoints: [
      'Differentiation from similar relationship-focused platforms needs comparison.',
      'Visual, voice, and media features should be verified before feature scoring.',
      'Memory behavior over extended sessions requires testing.',
      'Pricing and premium access limits need checkout confirmation.',
    ],
    features: [
      'Virtual relationships',
      'Personalized conversations',
      'Customizable AI characters',
      'Relationship-focused interactions',
      'Interactive character experiences',
    ],
  },
  {
    slug: 'openlover',
    reviewSlug: 'openlover-review',
    productName: 'OpenLover',
    logoLabel: 'OL',
    image: '/new-ai-platforms/openlover.webp',
    category: 'AI Companion Platform',
    readTime: '6 min read',
    platformUrl: '/go/openlover',
    platformCtaLabel: 'Visit OpenLover',
    featured: false,
    dateLabel: 'Jul 2026 batch',
    signalScore: 4.16,
    statusLabel: 'New scan',
    price: '',
    description:
      'OpenLover is an emerging AI companion platform focused on customizable virtual companions, conversational AI, and relationship-based interactions. The platform emphasizes personalized AI characters and ongoing conversations, making it an early entrant within the growing AI girlfriend category.',
    bestFor:
      'Readers exploring emerging AI girlfriend platforms with personalized characters and ongoing relationship-based conversations.',
    reviewTitle: 'OpenLover Review: Personalized AI Characters and Relationship-Based Chat',
    reviewExcerpt:
      'OpenLover is an emerging AI companion platform focused on customizable virtual companions, conversational AI, relationship-based interactions, and ongoing conversations.',
    reviewSummary:
      'OpenLover belongs in this batch as an emerging AI companion platform focused on customizable virtual companions, conversational AI, and relationship-based interactions with personalized AI characters and ongoing conversations. It is an early entrant in the AI girlfriend category, which makes it worth tracking now even if the public feature set is still maturing. The review should test onboarding speed, personality depth, conversation quality, and whether ongoing conversations feel like a continuing relationship.',
    standoutPoints: [
      'Emerging AI girlfriend positioning makes it worth tracking early.',
      'Customizable virtual companions support personalized relationship setups.',
      'Relationship-based interactions fit Heaven Girlfriend reader intent.',
      'Ongoing conversation framing creates a clear continuity test angle.',
    ],
    watchPoints: [
      'As a newer platform, public product detail may still be limited.',
      'Billing, cancellation, and privacy terms need account-level verification.',
      'Chat quality and personality consistency require hands-on testing.',
      'Visual and media features should be documented before scoring.',
    ],
    features: [
      'Customizable virtual companions',
      'Conversational AI',
      'Relationship-based interactions',
      'Personalized AI characters',
      'Ongoing conversations',
    ],
  },
  {
    slug: 'xotic-ai',
    reviewSlug: 'xotic-ai-review',
    productName: 'Xotic AI',
    logoLabel: 'XT',
    image: '/new-ai-platforms/xotic-ai.webp',
    category: 'AI Girlfriend Companion',
    readTime: '6 min read',
    platformUrl: '/go/xotic-ai',
    platformCtaLabel: 'Visit Xotic AI',
    featured: false,
    dateLabel: 'Jul 2026 batch',
    signalScore: 4.14,
    statusLabel: 'New scan',
    price: '',
    description:
      'Xotic AI is an AI companion platform that combines customizable virtual characters, conversational AI, and AI-powered media generation. Designed for relationship-focused interactions, it enables users to personalize AI companions and engage in immersive conversations through a browser-based experience.',
    bestFor:
      'Users who want customizable virtual characters, conversational AI, and AI-powered media generation in a relationship-focused browser experience.',
    reviewTitle: 'Xotic AI Review: Custom Characters, AI Chat, and Media Generation',
    reviewExcerpt:
      'Xotic AI combines customizable virtual characters, conversational AI, and AI-powered media generation for relationship-focused companion interactions in a browser-based experience.',
    reviewSummary:
      'Xotic AI fits this batch because it combines customizable virtual characters, conversational AI, and AI-powered media generation for relationship-focused interactions in a browser-based experience. That creates a useful review split between chat quality, visual output, and whether the platform feels like a companion product or a media tool with chat attached. The review should document identity consistency across generated content, free versus paid access, and how strongly the platform supports ongoing virtual companionship.',
    standoutPoints: [
      'Combines conversational AI and AI-powered media generation in one companion flow.',
      'Customizable virtual characters support personalized relationship setups.',
      'Browser-based access makes first-look testing straightforward.',
      'Relationship-focused interactions fit the AI girlfriend category.',
    ],
    watchPoints: [
      'Generated media quality and consistency should be tested across characters.',
      'Pricing, credits, and content limits require checkout verification.',
      'Privacy and content policy terms should be reviewed before recommendation.',
      'Differentiation from similar companion platforms needs comparison.',
    ],
    features: [
      'Customizable virtual characters',
      'Conversational AI',
      'AI-powered media generation',
      'Relationship-focused interactions',
      'Browser-based companion chat',
    ],
  },
  {
    slug: 'linky-ai',
    reviewSlug: 'linky-ai-review',
    productName: 'Linky AI',
    logoLabel: 'LK',
    image: '/new-ai-platforms/linky-ai.webp',
    category: 'AI Character Platform',
    readTime: '6 min read',
    platformUrl: '/go/linky-ai',
    platformCtaLabel: 'Visit Linky AI',
    featured: false,
    dateLabel: 'Jul 2026 batch',
    signalScore: 4.12,
    statusLabel: 'New scan',
    price: '',
    description:
      'Linky AI is an AI character and companion platform that offers a wide range of interactive virtual personalities for conversations, roleplay, and relationship-oriented experiences. Its growing character ecosystem provides users with multiple companion styles, from casual friendships to long-term virtual interactions.',
    bestFor:
      'Users who want a growing character ecosystem spanning casual friendships, roleplay, and long-term virtual companion interactions.',
    reviewTitle: 'Linky AI Review: Interactive Personalities, Roleplay, and Companion Chat',
    reviewExcerpt:
      'Linky AI offers a wide range of interactive virtual personalities for conversations, roleplay, and relationship-oriented experiences across a growing character ecosystem.',
    reviewSummary:
      'Linky AI belongs in this batch because it offers a wide range of interactive virtual personalities for conversations, roleplay, and relationship-oriented experiences across a growing character ecosystem that spans casual friendships and long-term virtual interactions. That makes it relevant for readers who want breadth before committing to one companion. The review should test whether ecosystem size translates into consistent quality and whether relationship-oriented experiences feel personal or depend entirely on character selection.',
    standoutPoints: [
      'Growing character ecosystem supports multiple companion styles.',
      'Interactive virtual personalities cover friendships, roleplay, and relationship chat.',
      'Relationship-oriented experiences fit Heaven Girlfriend companion comparisons.',
      'Breadth gives readers many entry points into AI companion use cases.',
    ],
    watchPoints: [
      'Character ecosystem size may not equal consistent conversation quality.',
      'Relationship focus depends heavily on character selection.',
      'Premium limits and moderation policies need verification.',
      'Long-term virtual interaction claims need multi-session testing.',
    ],
    features: [
      'Interactive virtual personalities',
      'Conversations and roleplay',
      'Relationship-oriented experiences',
      'Growing character ecosystem',
      'Casual and long-term companion styles',
    ],
  },
  {
    slug: 'polybuzz-ai',
    reviewSlug: 'polybuzz-ai-review',
    productName: 'PolyBuzz AI',
    logoLabel: 'PB',
    image: '/new-ai-platforms/polybuzz-ai.webp',
    category: 'Character-Driven AI Platform',
    readTime: '6 min read',
    platformUrl: '/go/polybuzz-ai',
    platformCtaLabel: 'Visit PolyBuzz AI',
    featured: false,
    dateLabel: 'Jul 2026 batch',
    signalScore: 4.1,
    statusLabel: 'New scan',
    price: '',
    description:
      'PolyBuzz AI is a large AI character platform featuring millions of community-created AI personalities for roleplay, storytelling, and virtual companionship. Although broader than a dedicated AI girlfriend app, its extensive character library and relationship-focused conversations make it a popular AI companion platform.',
    bestFor:
      'Users who want massive character variety, community-created personalities, and relationship-focused conversations across roleplay and storytelling.',
    reviewTitle: 'PolyBuzz AI Review: Millions of Characters, Roleplay, and Virtual Companionship',
    reviewExcerpt:
      'PolyBuzz AI is a large AI character platform with millions of community-created personalities for roleplay, storytelling, virtual companionship, and relationship-focused conversations.',
    reviewSummary:
      'PolyBuzz AI fits this batch because it is a large AI character platform featuring millions of community-created personalities for roleplay, storytelling, and virtual companionship, with relationship-focused conversations that keep it relevant even though it is broader than a dedicated AI girlfriend app. Heaven Girlfriend should review it through a companion lens: which characters support romantic or relationship-style interactions and how consistent quality is across user-created personalities.',
    standoutPoints: [
      'Millions of community-created personalities create huge discovery potential.',
      'Roleplay and storytelling support immersive companion setups.',
      'Relationship-focused conversations keep it relevant for companion readers.',
      'Popular scale makes it one of the most visible AI character platforms.',
    ],
    watchPoints: [
      'Broader than traditional AI girlfriend apps.',
      'Quality varies widely across community-created characters.',
      'Romantic focus depends on character choice and setup.',
      'Moderation, privacy, and premium limits need careful review.',
    ],
    features: [
      'Millions of community-created personalities',
      'Roleplay',
      'Storytelling',
      'Virtual companionship',
      'Relationship-focused conversations',
    ],
  },
  {
    slug: 'talkie-ai',
    reviewSlug: 'talkie-ai-review',
    productName: 'Talkie AI',
    logoLabel: 'TK',
    image: '/new-ai-platforms/talkie-ai.webp',
    category: 'AI Character Platform',
    readTime: '6 min read',
    platformUrl: '/go/talkie-ai',
    platformCtaLabel: 'Visit Talkie AI',
    featured: false,
    dateLabel: 'Jul 2026 batch',
    signalScore: 4.12,
    statusLabel: 'New scan',
    price: '',
    description:
      'Talkie AI is an AI character and companion platform that enables users to chat with customizable virtual personalities through text, voice, and interactive roleplay. With a large community of AI characters and relationship-oriented experiences, it has become one of the most widely used AI companion platforms for personalized conversations.',
    bestFor:
      'Users who want text, voice, and roleplay with customizable virtual personalities across one of the largest AI companion character communities.',
    reviewTitle: 'Talkie AI Review: Text, Voice, Roleplay, and AI Companion Characters',
    reviewExcerpt:
      'Talkie AI lets users chat with customizable virtual personalities through text, voice, and interactive roleplay across a large community of relationship-oriented AI characters.',
    reviewSummary:
      'Talkie AI belongs in this batch because it enables users to chat with customizable virtual personalities through text, voice, and interactive roleplay across a large community of AI characters and relationship-oriented experiences. Its scale makes it one of the most widely used AI companion platforms for personalized conversations, which gives Heaven Girlfriend a strong reason to review it even though it is broader than a dedicated AI girlfriend app. The review should test voice quality, conversation consistency, Talkie+ value, and how well relationship-oriented characters hold up over time.',
    standoutPoints: [
      'Text, voice, and interactive roleplay support multimodal companion testing.',
      'Large community of AI characters creates broad discovery potential.',
      'Relationship-oriented experiences fit Heaven Girlfriend companion comparisons.',
      'Widely used scale makes it one of the most visible AI companion platforms.',
    ],
    watchPoints: [
      'It is broader than girlfriend-only apps, so romantic focus depends on character choice.',
      'Talkie+ limits and premium value need checkout verification.',
      'Quality may vary widely across community-created characters.',
      'Mobile app versus web experience should be compared before scoring UX.',
    ],
    features: [
      'Customizable virtual personalities',
      'Text chat',
      'Voice chat',
      'Interactive roleplay',
      'Large AI character community',
    ],
  },
  {
    slug: 'dondi-ai',
    reviewSlug: 'dondi-ai-review',
    productName: 'Dondi AI',
    logoLabel: 'DA',
    image: '/new-ai-platforms/dondi-ai.webp',
    category: 'AI Companion Builder',
    readTime: '7 min read',
    platformUrl: '/go/dondi-ai',
    platformCtaLabel: 'Visit Dondi AI',
    featured: false,
    dateLabel: "Last week's new platforms",
    signalScore: 4.48,
    statusLabel: 'Last week',
    price: '',
    description:
      '18+ AI companion builder for creating ready-made or custom girlfriends, boyfriends, and roleplay partners with appearance controls, personality settings, voice styles, memory, images, and videos.',
    bestFor:
      'Readers who want a full companion-building flow rather than a fixed list of pre-made characters, especially if they care about memory, media generation, and adult roleplay controls.',
    reviewTitle: 'Dondi AI Review: Custom AI Girlfriends, Memory, Voice, Images, and Video',
    reviewExcerpt:
      'Dondi AI is a browser-based 18+ companion builder with custom characters, voice styles, contextual memory, image generation, video tools, and relationship-style roleplay.',
    reviewSummary:
      'Dondi AI covers nearly every feature category Heaven Girlfriend normally checks: companion creation, personality shaping, memory, roleplay, voice, images, videos, privacy claims, and recurring billing. The product is positioned as a relationship builder where users can create or choose companions and keep a continuing dynamic over time. The main editorial question is whether its included feature messaging matches checkout reality, because terms around tokens and subscription cycles need careful hands-on verification.',
    standoutPoints: [
      'Custom companion creation covers appearance, personality, roleplay style, and relationship framing.',
      'Memory, voice, image, and video features give it enough depth for a full Heaven Girlfriend review.',
      'The adult companion positioning is direct without needing to force it into the AI girlfriend category.',
      'Browser-first access makes it easier to test than mobile-only companion apps.',
    ],
    watchPoints: [
      'Subscription pricing and token usage need direct checkout confirmation.',
      'Claims around privacy and persistent memory should be tested against actual account behavior.',
      'Image and video identity consistency may vary depending on generated character setup.',
      'Account deletion, cancellation, and refund paths should be verified before a strong recommendation.',
    ],
    features: [
      'Custom AI girlfriend and boyfriend creation',
      'Ready-made companion profiles',
      'Contextual memory',
      'Voice style selection',
      'AI image generation',
      'AI video generation',
      'Adult roleplay scenarios',
    ],
  },
  {
    slug: 'secrets-ai',
    reviewSlug: 'secrets-ai-review',
    productName: 'Secrets AI',
    logoLabel: 'SA',
    image: '/new-ai-platforms/secrets-ai.webp',
    category: 'NSFW AI Girlfriend',
    readTime: '7 min read',
    platformUrl: '/go/secrets-ai',
    platformCtaLabel: 'Visit Secrets AI',
    featured: false,
    dateLabel: "Last week's new platforms",
    signalScore: 4.44,
    statusLabel: 'Last week',
    price: 'Free daily messages, Premium and Ultimate tiers reported',
    description:
      'Adult AI girlfriend platform with catalog companions, custom girlfriends, relationship bonding, memory tiers, user personas, images, video generation, and a media currency system.',
    bestFor:
      'Users who want a direct AI girlfriend product with relationship progression, memory, visual media, and enough public product detail to compare free versus paid access.',
    reviewTitle: 'Secrets AI Review: Memory Tiers, Moments, Images, and the Real Cost of Unlimited Chat',
    reviewExcerpt:
      'Secrets AI combines AI girlfriend chat, custom companions, memory tiers, media generation, user personas, and a Moments-based economy for images, video, audio, and calls.',
    reviewSummary:
      'Secrets AI is one of the clearest direct fits for the new tracker because it gives Heaven Girlfriend multiple concrete review angles instead of relying on vague AI companion marketing. The platform combines classic AI girlfriend chat with memory progression, custom personas, media generation, and a separate usage currency. The review needs to be strict about whether Premium and Ultimate tiers genuinely improve the relationship experience or mostly increase monthly allowances.',
    standoutPoints: [
      'Direct adult AI girlfriend positioning matches the page intent.',
      'Memory tiers create a strong hands-on testing angle around long-term relationship realism.',
      'User personas and bonding mechanics can make roleplay feel more personal than basic chat.',
      'Images, video, audio, and calls provide enough multimodal depth for a detailed review.',
    ],
    watchPoints: [
      'Moments/media currency can make the real monthly cost higher than the subscription headline.',
      'Ultimate-tier memory improvements need to be compared against free and Premium access.',
      'Age gate, deletion, refund, and cancellation behavior need account-level testing.',
      'Generated media quality should be checked across more than one companion type.',
    ],
    features: [
      'AI girlfriend catalog',
      'Custom companion creation',
      'Relationship bonding',
      'Memory tiers',
      'User personas',
      'AI images and video',
      'Moments media currency',
    ],
  },
  {
    slug: 'kalon-ai',
    reviewSlug: 'kalon-ai-review',
    productName: 'Kalon AI',
    logoLabel: 'KA',
    image: '/new-ai-platforms/kalon-ai.webp',
    category: 'AI Girlfriend Builder',
    readTime: '6 min read',
    platformUrl: '/go/kalon-ai',
    platformCtaLabel: 'Visit Kalon AI',
    featured: false,
    dateLabel: "Last week's new platforms",
    signalScore: 4.4,
    statusLabel: 'Last week',
    price: 'Signup coins plus monthly and annual coin plans reported',
    description:
      'AI girlfriend builder with custom personality, appearance, backstory, relationship style, voice, pictures, memory, mood presets, boundaries, and optional adult roleplay.',
    bestFor:
      'Readers who want a flexible girlfriend builder that can lean romantic, supportive, fantasy, anime, realistic, or NSFW depending on setup.',
    reviewTitle: 'Kalon AI Review: Can One Coin Plan Cover Chat, Voice, Images, and Video?',
    reviewExcerpt:
      'Kalon AI offers an AI girlfriend builder with personality controls, memory, voice, pictures, mood presets, boundaries, and optional adult roleplay.',
    reviewSummary:
      'Kalon AI is structured around the kind of setup choices that usually determine whether an AI girlfriend feels generic or personal. Instead of only offering public characters, it emphasizes appearance, personality, backstory, relationship style, voice, mood, and boundaries. The biggest value question is the coin model, because even predictable plans can become expensive if chat, media, voice, and video all draw from the same balance.',
    standoutPoints: [
      'Builder controls cover personality, backstory, mood, boundaries, and relationship style.',
      'Supports both softer romantic companion setups and adult roleplay use cases.',
      'Voice and visual features give the platform more depth than text-only AI girlfriend chat.',
      'Signup and daily coin messaging creates a clear free-access test path.',
    ],
    watchPoints: [
      'Coin consumption should be measured across ordinary chat, images, voice, and video.',
      'Pricing claims should be verified at the actual checkout, not only comparison pages.',
      'Image consistency and memory behavior need multi-session testing.',
      'Refund, cancellation, and deletion controls should be checked before recommending paid plans.',
    ],
    features: [
      'AI girlfriend builder',
      'Personality and backstory controls',
      'Relationship style settings',
      'Memory and mood presets',
      'Voice features',
      'AI pictures and video',
      'Optional NSFW roleplay',
    ],
  },
  {
    slug: 'kissable',
    reviewSlug: 'kissable-review',
    productName: 'Kissable',
    logoLabel: 'KS',
    image: '/new-ai-platforms/kissable.webp',
    category: 'Romantic AI Companion',
    readTime: '7 min read',
    platformUrl: '/go/kissable',
    platformCtaLabel: 'Visit Kissable',
    featured: false,
    dateLabel: "Last week's new platforms",
    signalScore: 4.35,
    statusLabel: 'Last week',
    price: 'Free tier, monthly/yearly Premium, optional media currency reported',
    description:
      'Adult romantic companion app for creating an AI girlfriend, boyfriend, or friend with persistent memory, photos, voice notes, couple photos, short videos, scenarios, and proactive messages.',
    bestFor:
      'Users who care most about relationship continuity, proactive messages, memory, and a companion that feels less like a disposable fantasy bot.',
    reviewTitle: 'Kissable Review: Does Its AI Girlfriend Really Remember Everything?',
    reviewExcerpt:
      'Kissable is a romantic AI companion app with girlfriend, boyfriend, and friend creation, memory, photos, voice notes, couple media, scenarios, and proactive messages.',
    reviewSummary:
      "Kissable is one of the strongest romantic-continuity candidates on the tracker. The platform's core promise is a persistent companion relationship across memory, proactive messaging, voice notes, photos, couple images, short videos, and scenarios. The review needs to test whether the companion remembers enough to feel like an ongoing relationship, and separate unlimited-message claims from media-currency limits.",
    standoutPoints: [
      'Girlfriend, boyfriend, and friend creation gives it broader companion coverage than girlfriend-only apps.',
      'Persistent memory and proactive messaging create a strong relationship-continuity angle.',
      'Photos, couple photos, voice notes, short videos, and scenarios support a complete companion flow.',
      'Cross-platform access makes it easier to compare web and mobile behavior.',
    ],
    watchPoints: [
      'Memory should be tested over multiple days, not only in one session.',
      'Media currency may limit photos, voice, and video despite generous messaging language.',
      'Uploaded reference photo handling and privacy terms deserve careful review.',
      'Explicit imagery restrictions may surprise users expecting a fully adult visual platform.',
    ],
    features: [
      'AI girlfriend, boyfriend, and friend creation',
      'Persistent companion memory',
      'Proactive messages',
      'Voice notes',
      'Solo and couple photos',
      'Short video generation',
      'Scenario-based interactions',
    ],
  },
];

export const archivedNewPlatformTrackerItems: NewPlatformTrackerItem[] = [
  {
    slug: 'justsext',
    reviewSlug: 'justsext-review',
    productName: 'JustSext',
    logoLabel: 'JS',
    image: '/new-ai-platforms/justsext.webp',
    category: 'Creator AI Girlfriend',
    readTime: '5 min read',
    platformUrl: '/go/justsext',
    platformCtaLabel: 'Visit JustSext',
    featured: true,
    dateLabel: 'Apr 2026 launch',
    signalScore: 4.45,
    statusLabel: 'New launch',
    price: 'Token packages reported from $25',
    description:
      'Creator-based AI girlfriend platform built around licensed digital twins, private chat, voice-style interactions, and custom visual requests.',
    bestFor:
      'Readers who want the most direct creator-to-AI girlfriend crossover instead of another generic character library.',
    reviewTitle: 'JustSext Review: Real-Creator AI Girlfriends Arrive in 2026',
    reviewExcerpt:
      'JustSext is a new creator-led AI girlfriend platform where licensed digital twins chat, flirt, and generate custom media for fans.',
    reviewSummary:
      'JustSext is the most on-theme pick for the new list because it is built around AI girlfriend behavior, not a broad chatbot ecosystem. The product angle is creator licensing: users interact with AI versions of real adult creators through chat, voice-style experiences, and custom visual requests. Consent first: only upload photos of yourself, or of someone who has given clear permission. Creating sexual images of a real person without consent is harmful and illegal in many countries. That makes it more current than a standard gallery of fictional characters, but it also needs stricter review around consent, billing, age controls, and how authentic each creator twin actually feels after the first few messages.',
    standoutPoints: [
      'Real-creator positioning gives the platform a sharper hook than standard AI character marketplaces.',
      'Chat, calls, and custom visuals match the core AI girlfriend use case.',
      'Creator licensing creates a clear editorial angle around consent and revenue sharing.',
    ],
    watchPoints: [
      'Age gate, creator consent, and content controls need careful verification.',
      'Token pricing can become expensive if custom media is the main draw.',
      'Personality depth may vary from one creator twin to another.',
    ],
    features: [
      'Licensed creator AI twins',
      'Private AI girlfriend chat',
      'Custom image and video requests',
      'Voice-style companion interactions',
    ],
  },
  {
    slug: 'soulkyn',
    reviewSlug: 'soulkyn-review',
    productName: 'Soulkyn',
    logoLabel: 'SK',
    image: '/new-ai-platforms/soulkyn.webp',
    category: 'AI Companion Builder',
    readTime: '6 min read',
    platformUrl: '/go/soulkyn',
    platformCtaLabel: 'Visit Soulkyn',
    featured: false,
    dateLabel: '2026 tracker',
    signalScore: 4.35,
    statusLabel: 'New to HG',
    price: 'Free start, premium credits available',
    description:
      'AI companion platform for creating personalized Kyns with chat, memory, image generation, video generation, and voice chat.',
    bestFor:
      'Users who want a custom AI girlfriend or fantasy partner with more creator-style control than a fixed profile library.',
    reviewTitle: 'Soulkyn Review: Custom AI Girlfriends, Kyns, Images, and Voice',
    reviewExcerpt:
      'Soulkyn focuses on custom AI companions called Kyns, combining roleplay, memory, image tools, video tools, and voice chat.',
    reviewSummary:
      'Soulkyn fits the new page because it directly advertises custom AI girlfriend creation and gives users a wide companion toolkit. The experience is closer to building a private fantasy world than choosing one static chatbot. Its strongest angle is breadth: users can explore public Kyns, create their own, generate images and videos, and use voice chat. The tradeoff is that a large community-driven catalog can feel uneven, so a review needs to separate the best Kyn experiences from weaker public characters.',
    standoutPoints: [
      'Custom Kyn creation makes it feel more personal than a fixed chatbot list.',
      'Image, video, and voice features support a fuller AI girlfriend experience.',
      'The public character ecosystem gives users many styles to explore.',
    ],
    watchPoints: [
      'Community-created companions may vary heavily in quality.',
      'Credit usage should be checked before calling it good value.',
      'Adult (18+) roleplay features require careful privacy and safety review.',
    ],
    features: [
      'Create custom Kyns',
      'AI image generator',
      'AI video generator',
      'Voice chat',
      'Public companion discovery',
    ],
  },
  {
    slug: 'rubii-ai',
    reviewSlug: 'rubii-ai-review',
    productName: 'Rubii AI',
    logoLabel: 'RB',
    image: '/new-ai-platforms/rubii-ai.webp',
    category: 'Character Roleplay',
    readTime: '5 min read',
    platformUrl: '/go/rubii-ai',
    platformCtaLabel: 'Visit Rubii',
    featured: false,
    dateLabel: '2026 tracker',
    signalScore: 4.2,
    statusLabel: 'New to HG',
    price: 'Free entry with paid options',
    description:
      'Free AI character chat and roleplay platform where users can create AI girlfriends, boyfriends, original characters, images, videos, and voice assets.',
    bestFor:
      'Readers who want a broader creative roleplay app that still supports AI girlfriend-style characters and media.',
    reviewTitle: 'Rubii AI Review: Free AI Character Chat With Girlfriend Creation',
    reviewExcerpt:
      'Rubii AI combines AI companion chat, character creation, roleplay scenes, image generation, video tools, and mobile access.',
    reviewSummary:
      'Rubii AI is a stronger fit than generic AI assistant tools because its public positioning clearly includes AI girlfriend creation. It is not only a chat window; it combines character discovery, roleplay scenes, creator profiles, generated images, short videos, and voice tools. That makes it useful for readers who want a creative sandbox rather than a single polished girlfriend flow. The main thing to test is whether its girlfriend-style experiences feel emotionally coherent or whether the community roleplay layer makes quality inconsistent.',
    standoutPoints: [
      'Supports AI girlfriend, AI boyfriend, and original character creation.',
      'Image, video, and voice tools make it more than text-only roleplay.',
      'Free entry lowers friction for users comparing new platforms.',
    ],
    watchPoints: [
      'Community content quality may be inconsistent.',
      'It is broader than girlfriend-only apps, so romantic focus may depend on the character.',
      'Moderation and privacy controls should be checked before strong recommendation.',
    ],
    features: [
      'AI character chat',
      'AI girlfriend creation',
      'AI image generation',
      'Short video tools',
      'Mobile app access',
    ],
  },
  {
    slug: 'couple-me',
    reviewSlug: 'couple-me-review',
    productName: 'Couple.me',
    logoLabel: 'CM',
    image: '/new-ai-platforms/couple-me.webp',
    category: 'AI Girlfriend Generator',
    readTime: '5 min read',
    platformUrl: '/go/couple-me',
    platformCtaLabel: 'Visit Couple.me',
    featured: false,
    dateLabel: '2025-2026',
    signalScore: 4.15,
    statusLabel: 'New to HG',
    price: 'Free start, premium plan promoted',
    description:
      'AI girlfriend app for creating a dream partner, customizing looks and personality, chatting instantly, and requesting custom selfies.',
    bestFor:
      'Users who want a direct AI girlfriend generator with simple onboarding and image-focused companion features.',
    reviewTitle: 'Couple.me Review: Free AI Girlfriend Creation and Custom Selfies',
    reviewExcerpt:
      'Couple.me is a direct AI girlfriend creator with custom appearance, personality, interests, chat, memory-style personalization, and AI photos.',
    reviewSummary:
      'Couple.me is one of the cleanest fits for the page because its positioning is direct: create your AI girlfriend, customize her, chat, and request images. It does not require explaining why it belongs in the category. The platform appears designed for fast onboarding, with character browsing, companion creation, image generation, and premium upsells. The review angle should focus on how much control users really get over personality and whether the photo features deliver enough quality to compete with stronger visual-first platforms.',
    standoutPoints: [
      'Very direct AI girlfriend positioning with clear creation flow.',
      'Custom looks, personality, interests, and images match high-intent user expectations.',
      'The site includes affiliate and payment infrastructure, suggesting a monetized product rather than a demo.',
    ],
    watchPoints: [
      'The published premium discount should be checked against the checkout price.',
      'Photo quality and memory behavior need hands-on testing.',
      'Safety and privacy claims should be verified before recommending broadly.',
    ],
    features: [
      'Create AI girlfriend',
      'Customize looks and personality',
      'AI girlfriend pictures',
      'Browse characters',
      'Premium plan',
    ],
  },
  {
    slug: 'ai-girlfriend-com',
    reviewSlug: 'ai-girlfriend-com-review',
    productName: 'AI-Girlfriend.com',
    logoLabel: 'AG',
    image: '/new-ai-platforms/ai-girlfriend-com.webp',
    category: 'Adult AI Girlfriend',
    readTime: '5 min read',
    platformUrl: '/go/ai-girlfriend-com',
    platformCtaLabel: 'Visit AI Girlfriend',
    featured: false,
    dateLabel: '2026 tracker',
    signalScore: 4.05,
    statusLabel: 'New to HG',
    price: 'Account required, adult site',
    description:
      'Adult AI girlfriend site with character browsing, AI girlfriend creation, private chat, and generated media through a dedicated gallery flow.',
    bestFor:
      'Readers who want the most literal AI girlfriend product naming and a character-first adult companion interface.',
    reviewTitle: 'AI-Girlfriend.com Review 2026',
    reviewExcerpt:
      'AI Girlfriend is a direct adult companion site built around characters, AI girlfriend creation, chat, and media generation.',
    reviewSummary:
      'AI Girlfriend belongs on the new list because the product is not ambiguous: it is a dedicated adult AI girlfriend site with character browsing, chat, media, and account-based access. The interesting part is not the brand name; it is whether the platform can turn a simple character directory into a real companion experience. The review should test onboarding friction, character variety, image generation, billing transparency, and whether conversations feel personal after the initial profile novelty wears off.',
    standoutPoints: [
      'The product is directly aligned with AI girlfriend search intent.',
      'Character browsing and media generation are built into the main navigation.',
      'Age-gated adult positioning makes it relevant for mature companion comparisons.',
    ],
    watchPoints: [
      'Public content is limited behind account and age gates.',
      'Billing and media credit details need verification.',
      'The generic brand name can make trust and differentiation harder.',
    ],
    features: [
      'AI girlfriend creation',
      'Character browsing',
      'Private chat',
      'Generated images',
      'Adult age gate',
    ],
  },
  {
    slug: 'intimate-ai',
    reviewSlug: 'intimate-ai-review',
    productName: 'Intimate AI',
    logoLabel: 'IA',
    image: '/new-ai-platforms/intimate-ai.webp',
    category: 'Dating Simulation',
    readTime: '5 min read',
    platformUrl: '/go/intimate-ai',
    platformCtaLabel: 'Visit Intimate AI',
    featured: false,
    dateLabel: '2025-2026',
    signalScore: 4.0,
    statusLabel: 'New to HG',
    price: 'Checkout shown from $99',
    description:
      'Virtual AI girlfriend experience with dating-simulation framing, voice messages, photo exchange, personalized settings, and memory-style galleries.',
    bestFor:
      'Users who want a more intimate dating-sim presentation instead of a crowded marketplace of public characters.',
    reviewTitle: 'Intimate AI Review: Voice, Photo Exchange, and Dating-Sim Energy',
    reviewExcerpt:
      'Intimate AI presents an AI girlfriend experience with voice messages, photo exchange, personalization, privacy messaging, and dating-sim framing.',
    reviewSummary:
      'Intimate AI is relevant because it frames the experience around one-to-one connection rather than a massive public character library. The site leans into dating simulation, photo exchange, voice messages, and private memories, which makes it feel closer to a premium AI girlfriend flow than a generic chatbot. The biggest review question is value: the visible checkout language is high enough that Heaven Girlfriend should test whether the experience justifies its pricing compared with stronger ranked platforms.',
    standoutPoints: [
      'Voice messages and photo exchange are core AI girlfriend features.',
      'One-to-one dating-sim framing feels more focused than broad roleplay catalogs.',
      'Personalized settings and private memories give the product a relationship-style hook.',
    ],
    watchPoints: [
      'Visible checkout pricing appears high and needs confirmation.',
      'Claims around privacy and confidentiality should be reviewed closely.',
      'The experience may be narrower than platforms with large character libraries.',
    ],
    features: [
      'Virtual AI girlfriend chat',
      'Voice messages',
      'Photo exchange',
      'Personalized companion settings',
      'Private memories gallery',
    ],
  },
  {
    slug: 'dreamle',
    reviewSlug: 'dreamle-review',
    productName: 'Dreamle',
    logoLabel: 'DL',
    image: '/new-ai-platforms/dreamle.webp',
    category: 'NSFW AI Girlfriend Chat',
    readTime: '5 min read',
    platformUrl: '/go/dreamle',
    platformCtaLabel: 'Visit Dreamle',
    featured: false,
    dateLabel: '2026 tracker',
    signalScore: 4.1,
    statusLabel: 'New review',
    price: 'Flexible pricing promoted',
    description:
      'NSFW AI character and girlfriend chat platform with custom companions, real-time roleplay, AI image generation, and fantasy-world features.',
    bestFor:
      'Readers who want a more fantasy-heavy AI girlfriend chat experience with custom characters and intense roleplay options.',
    reviewTitle: 'Dreamle Review: AI Girlfriend Roleplay, Custom Characters, and Fantasy Worlds',
    reviewExcerpt:
      'Dreamle offers NSFW AI girlfriend chat, custom companions, real-time roleplay, fantasy-world features, and AI image generation.',
    reviewSummary:
      'Dreamle is built around character chat, custom companions, and girlfriend-style scenarios. The platform mixes public characters, custom character creation, fantasy-world features, group-style interactions, and image generation. The review should test whether that setup becomes a polished companion experience.',
    standoutPoints: [
      'Direct NSFW AI girlfriend and companion positioning fits the category.',
      'Custom character creation and fantasy-world features give it more depth than simple chat.',
      'AI image generation adds a visual layer to the roleplay experience.',
    ],
    watchPoints: [
      'The adult-first tone may be too aggressive for users wanting softer romance.',
      'Roleplay quality needs hands-on testing across multiple characters.',
      'Pricing and image-generation limits should be verified before recommending it strongly.',
    ],
    features: [
      'NSFW AI girlfriend chat',
      'Custom AI character creation',
      'Fantasy World mode',
      'AI image generation',
      'Real-time roleplay',
    ],
  },
];

export const newPlatformReviewItems: NewPlatformTrackerItem[] = [
  ...newPlatformTrackerItems,
  ...lastWeekNewPlatformTrackerItems,
  ...archivedNewPlatformTrackerItems,
];

export function orderThisWeekNewPlatformItems(items: NewPlatformTrackerItem[]): NewPlatformTrackerItem[] {
  if (items.length === 0) return [];
  const [cover, ...rest] = items;
  return [cover, ...[...rest].sort((a, b) => b.signalScore - a.signalScore)];
}

